import React, { useEffect, useState, useRef, useMemo, useContext } from 'react';
import ReactMarkdown from 'react-markdown';
import { AuthContext } from '../contexts/AuthContext';
import clientHelperService from '../services/clientHelperService';
import vectorService from '../services/vectorService';
import bedrockService from '../services/bedrockService';
import promptService from '../services/promptService';
import FilePreviewModal from '../components/FilePreviewModal';
import PaginationControls from '../components/PaginationControls';
import SummaryEditor, { htmlToMarkdown, textToHtml } from '../components/SummaryEditor';

// Fallback options used if the API returns empty or fails
const DEFAULT_DOC_TYPE_OPTIONS = [
    "Bank",
    "CC",
    "Education",
    "Estate",
    "Employer",
    "Health",
    "Income",
    "Insurance",
    "Investment",
    "Loan",
    "Mixed",
    "Tax",
];

// Fallback sub-type options
const DEFAULT_SUB_TYPE_OPTIONS = [
  "1040",
    "401k",
    "403b",
    "457",
    "529",
    "Annuity",
    "Auto",
    "Brokerage",
    "CD",
    "Checking",
    "Credit Card Statement",
    "Deed",
    "ESA",
    "Federal",
    "Home",    
    "HSA",
    "IRA",
    "Long Term Care",
    "Medical",
    "Mixed",
    "Money Market",
    "Paystub",
    "Pension",
    "Personal",
    "POA",
    "Roth 401k",
    "Roth IRA",
    "RSU",
    "Savings",    
    "SS",
    "State",
    "Student",
    "Tax Return",
    "Treasury Bills",
    "Trust",
    "Umbrella",
    "W-2",
    "Will",
];

// Module-level cached promise to avoid duplicate network calls across
// component unmount/remount cycles (React Strict Mode in development).
let docTypesLoadPromise = null;

const VectorInspector = () => {
    const { hasPermission } = useContext(AuthContext);
    // Use-only access is enough here; editing app-owned prompts stays in Prompt Manager.
    const canUseAppPrompts = hasPermission('prompt_admin')
        || hasPermission('prompt_user')
        || hasPermission('admin');

    const [clients, setClients] = useState([]);
    const [selectedClient, setSelectedClient] = useState('');
    const [kbFiles, setKbFiles] = useState([]);
    const [selectedFile, setSelectedFile] = useState('');
    const [selectedFileId, setSelectedFileId] = useState('');
    const [fileModalOpen, setFileModalOpen] = useState(false);
    const [fileModalUri, setFileModalUri] = useState(null);
    const [fileModalFilename, setFileModalFilename] = useState('');
    const [docType, setDocType] = useState('');
    const [vectors, setVectors] = useState([]);
    const [analysisResults, setAnalysisResults] = useState({});
    const [analyzingVectors, setAnalyzingVectors] = useState(new Set());
    const [vectorPurpose, setVectorPurpose] = useState('');
    const [documentPurpose, setDocumentPurpose] = useState('');
    const [analyzingVectorPurpose, setAnalyzingVectorPurpose] = useState(false);
    const [analyzingDocumentPurpose, setAnalyzingDocumentPurpose] = useState(false);
    const [manualReviews, setManualReviews] = useState({});
    const [selectedAnalysis, setSelectedAnalysis] = useState(null);
    const [showAnalysisModal, setShowAnalysisModal] = useState(false);
    const [selectedTextFull, setSelectedTextFull] = useState('');
    const [selectedTextKey, setSelectedTextKey] = useState('');
    const [showTextModal, setShowTextModal] = useState(false);
    const [fileAttributes, setFileAttributes] = useState(null);
    const [docTypesList, setDocTypesList] = useState(DEFAULT_DOC_TYPE_OPTIONS);
    const [docTypesData, setDocTypesData] = useState([]); // Full doc type objects with embedded sub_types
    const [subTypesList, setSubTypesList] = useState(DEFAULT_SUB_TYPE_OPTIONS);
    // Doc Types modal state
    const [docTypesModalOpen, setDocTypesModalOpen] = useState(false);
    const [docTypeModalInput, setDocTypeModalInput] = useState('');
    const [docTypeModalBusy, setDocTypeModalBusy] = useState(false);
    const [renamingDocType, setRenamingDocType] = useState(null);
    const [renameDocTypeInput, setRenameDocTypeInput] = useState('');
    // Sub Types modal state
    const [subTypesModalOpen, setSubTypesModalOpen] = useState(false);
    const [subTypeModalInput, setSubTypeModalInput] = useState('');
    const [subTypeModalBusy, setSubTypeModalBusy] = useState(false);
    const [renamingSubType, setRenamingSubType] = useState(null);
    const [renameSubTypeInput, setRenameSubTypeInput] = useState('');
    const [kbFilesSummary, setKbFilesSummary] = useState([]);
    const [loadingKbFilesSummary, setLoadingKbFilesSummary] = useState(false);
    
    // Pagination state for vectors
    const [vectorsPagination, setVectorsPagination] = useState({
        limit: 1000,
        offset: 0,
        totalCount: 0,
        hasMore: false
    });
    const [loadingMoreVectors, setLoadingMoreVectors] = useState(false);
    
    // Pagination state for file summaries
    const [fileSummariesPagination, setFileSummariesPagination] = useState({
        limit: 500,
        offset: 0,
        totalCount: 0,
        hasMore: false
    });
    const [loadingMoreFileSummaries, setLoadingMoreFileSummaries] = useState(false);
    // Sorting state for Knowledge-base Files table
    const [kbSort, setKbSort] = useState({ by: 'name', dir: 'asc' });
    // Filters for KB files table
    const [filterDocType, setFilterDocType] = useState('');
    const [filterSubType, setFilterSubType] = useState('');
    const [filterAccountNumber, setFilterAccountNumber] = useState('');
    // filename wildcard filter (supports * and ?; case-insensitive)
    const [filterFilename, setFilterFilename] = useState('');

    /**
     * Get filtered sub_types based on selected doc_type ID or name.
     * Returns sub_types array of {id, name} from the matching doc_type object.
     */
    const getFilteredSubTypes = (selectedDocTypeIdOrName) => {
        // If docTypesData not loaded yet, return empty array
        if (!docTypesData || docTypesData.length === 0) {
            return [];
        }
        
        if (!selectedDocTypeIdOrName) {
            // No doc_type selected, return all sub_types from all doc_types
            const allSubs = [];
            docTypesData.forEach(dt => {
                if (dt.sub_types) {
                    dt.sub_types.forEach(st => {
                        if (typeof st === 'object' && (st.id || st.name)) {
                            // Use id if available, otherwise use name as fallback id
                            const subId = st.id || st.name;
                            if (!allSubs.find(s => (s.id || s.name) === subId)) {
                                allSubs.push({ ...st, id: subId });
                            }
                        }
                    });
                }
            });
            return allSubs;
        }
        
        // Find the doc_type object that matches the selected ID or name
        const docTypeObj = docTypesData.find(dt => dt.id === selectedDocTypeIdOrName || dt.name === selectedDocTypeIdOrName);
        if (docTypeObj && docTypeObj.sub_types && docTypeObj.sub_types.length > 0) {
            // Return sub_type objects with id (use name as fallback if no id)
            return docTypeObj.sub_types
                .filter(st => typeof st === 'object' && (st.id || st.name))
                .map(st => ({ ...st, id: st.id || st.name }));
        }
        // Doc type found but has no sub_types, return empty array
        return [];
    };

    const toggleKbSort = (by) => {
        setKbSort(prev => {
            if (prev.by === by) {
                return { by, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
            }
            return { by, dir: 'asc' };
        });
    };

    const sortedKbFiles = useMemo(() => {
        if (!kbFilesSummary || kbFilesSummary.length === 0) return [];
        // prepare filename wildcard regex if provided
        let filenameRe = null;
        if (filterFilename && filterFilename.trim() !== '') {
            try {
                // escape regex chars then replace wildcard tokens
                const esc = filterFilename.trim().replace(/[.+^${}()|[\]\\]/g, '\\$&');
                const pat = esc.replace(/\\\*/g, '.*').replace(/\\\?/g, '.');
                // assume implicit leading/trailing wildcards so pattern matches anywhere
                const finalPat = `.*${pat}.*`;
                filenameRe = new RegExp(finalPat, 'i');
            } catch (e) {
                filenameRe = null;
            }
        }

        // apply filters first
        let arr = kbFilesSummary.filter(f => {
            // filename wildcard filter
            if (filenameRe) {
                const name = (f.name || getFilenameFromUri(f.s3_key || '') || '').toString();
                if (!filenameRe.test(name)) return false;
            }
            if (filterDocType && filterDocType !== '') {
                const docType = f.doc_type || 'Mixed';
                if (docType !== filterDocType) return false;
            }
            if (filterSubType && filterSubType !== '') {
                const subType = f.sub_type || 'Mixed';
                if (subType !== filterSubType) return false;
            }
            if (filterAccountNumber && filterAccountNumber !== '') {
                const accountNum = f.account_number || '';
                if (!accountNum.toLowerCase().includes(filterAccountNumber.toLowerCase())) return false;
            }
            return true;
        });
        arr = [...arr];
        const dir = kbSort.dir === 'asc' ? 1 : -1;
        const by = kbSort.by;
        arr.sort((a, b) => {
            const aa = a[by];
            const bb = b[by];
            // handle null/undefined
            if (aa == null && bb == null) return 0;
            if (aa == null) return -1 * dir;
            if (bb == null) return 1 * dir;
            // numeric compare
            if (typeof aa === 'number' && typeof bb === 'number') return (aa - bb) * dir;
            // date epoch compare
            if (by === 'dateEpochMs') {
                const na = Number(a.dateEpochMs || 0);
                const nb = Number(b.dateEpochMs || 0);
                return (na - nb) * dir;
            }
            // fallback string compare
            const sa = String(aa).toLowerCase();
            const sb = String(bb).toLowerCase();
            if (sa < sb) return -1 * dir;
            if (sa > sb) return 1 * dir;
            return 0;
        });
        return arr;
    }, [kbFilesSummary, kbSort, filterDocType, filterSubType, filterAccountNumber, filterFilename]);

    // Dirty-save batching: accumulate changes, wait until unchanged for 10s, then persist.
    const pendingAttrsRef = useRef(null); // accumulated attributes to save
    const pendingS3Ref = useRef(null); // which s3_path these pending attrs belong to
    const dirtyTsRef = useRef(0); // last change timestamp
    const saveTimerRef = useRef(null);
    const prevSelectedFileRef = useRef('');

    // Analysis queue controls: ensure only one analyze API call at a time
    const analysisQueueRef = useRef([]); // array of vectors to analyze
    const analysisInFlightRef = useRef(false); // whether one analyze call is in progress
    const analysisActiveFileRef = useRef(null); // s3_key for which the queue is active

    const DEFAULT_SAVE_DELAY_MS = 10000; // 10s

    const scheduleFlushIfIdle = () => {
        // clear existing timer
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
        }
        // schedule a checker that ensures no new dirty updates in the delay window
        const capturedTs = dirtyTsRef.current;
        saveTimerRef.current = setTimeout(async () => {
            // if dirtyTs hasn't changed during the wait window, flush
            if (dirtyTsRef.current === capturedTs) {
                await flushPendingSave();
            }
        }, DEFAULT_SAVE_DELAY_MS);
    };

    const markDirty = (attrs) => {
        // merge into pendingAttrsRef (shallow merge)
        const base = pendingAttrsRef.current || { ...(fileAttributes || {}) };
        // Always include client_id from selectedClient
        const withClient = { ...base, ...(attrs || {}), client_id: selectedClient || undefined };
        pendingAttrsRef.current = withClient;
        pendingS3Ref.current = selectedFile;
        dirtyTsRef.current = Date.now();
        scheduleFlushIfIdle();
    };

    const flushPendingSave = async () => {
        if (!pendingAttrsRef.current || !pendingS3Ref.current) return;
        // capture and clear before awaiting to avoid races
        const attrsToSave = pendingAttrsRef.current;
        const s3 = pendingS3Ref.current;
        pendingAttrsRef.current = null;
        pendingS3Ref.current = null;
        if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }

        try {
            const defaults = { vector_purpose_accepted: false, file_purpose_accepted: false, best_purpose: '' };
            const finalAttrs = { ...defaults, ...attrsToSave };
            // If the attributes currently reflect an existing file (fileAttributes not null
            // for that s3), then update, else create. We optimistically update UI if current
            // selectedFile matches the saved s3.
            let resp = null;
            // If saving for a different s3_path than current selectedFile, try to fetch fileAttributes
            const isCurrent = s3 === selectedFile;
            const existing = isCurrent ? fileAttributes : null;
            if (existing) {
                resp = await vectorService.updateFile(s3, finalAttrs);
            } else {
                resp = await vectorService.createFile(s3, finalAttrs);
            }
            if (resp && resp.success && resp.file) {
                const normalized = normalizeFileFromApi(resp.file) || {};
                // If this was for the currently selected file, update UI state
                if (isCurrent) setFileAttributes(normalized);
            }
        } catch (err) {
            console.warn('Failed to flush pending file save', err);
        }
    };

    const flushPendingSaveImmediateFor = async (s3Path) => {
        // If pending belongs to s3Path, force flush now
        if (pendingS3Ref.current && pendingS3Ref.current === s3Path) {
            if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
            await flushPendingSave();
        }
    };

    // Flush pending saves on unmount / navigation
    useEffect(() => {
        const handler = async (e) => {
            // flush immediately
            if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
            if (pendingAttrsRef.current) {
                // best-effort synchronous flush isn't possible; still attempt async flush
                await flushPendingSave();
            }
        };
        window.addEventListener('beforeunload', handler);
        return () => {
            window.removeEventListener('beforeunload', handler);
            // component unmount flush
            if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
            if (pendingAttrsRef.current) {
                // fire and forget
                flushPendingSave();
            }
        };
    }, []);

    // Normalize file response from API into the internal attributes object
    const normalizeFileFromApi = (file) => {
        if (!file) return null;
        const attrs = file.attributes ? { ...file.attributes } : {};
        // Extract doc_type, sub_type, account_number from top-level file object
        if (file.doc_type !== undefined && file.doc_type !== null) attrs.doc_type = file.doc_type;
        if (file.doc_type_id !== undefined && file.doc_type_id !== null) attrs.doc_type_id = file.doc_type_id;
        if (file.sub_type !== undefined && file.sub_type !== null) attrs.sub_type = file.sub_type;
        if (file.sub_type_id !== undefined && file.sub_type_id !== null) attrs.sub_type_id = file.sub_type_id;
        if (file.account_number !== undefined && file.account_number !== null) attrs.account_number = file.account_number;
        // Extract document summary fields from top-level file object
        if (file.doc_summary !== undefined) attrs.doc_summary = file.doc_summary;
        if (file.edited_document_summary !== undefined) attrs.edited_document_summary = file.edited_document_summary;
        // map notes fields from attributes
        if (file.attributes) {
            if (file.attributes.vector_purpose_notes !== undefined) attrs.vector_purpose_notes = file.attributes.vector_purpose_notes;
            if (file.attributes.file_purpose_notes !== undefined) attrs.file_purpose_notes = file.attributes.file_purpose_notes;
        }
        return attrs;
    };

    const defaultFileAttributes = () => ({
        vector_purpose: '',
        file_purpose: '',
        vector_purpose_accepted: false,
        file_purpose_accepted: false,
        best_purpose: '',
        vector_purpose_notes: '',
        file_purpose_notes: ''
    });

    const didFetchClients = useRef(false);

    const stats = useMemo(() => {
        const totalVectors = Array.isArray(vectors) ? vectors.length : 0;
        const totalTextLength = (vectors || []).reduce((acc, v) => acc + ((v && v.text) ? v.text.length : 0), 0);

        // Try to read epoch from the first vector's `data` property. Accept seconds or ms.
        const rawEpoch = vectors?.[0]?.date ?? null;
        let epochDisplay = 'N/A';
        if (rawEpoch != null) {
            let n = Number(rawEpoch);
            if (!Number.isNaN(n)) {
                // if seconds (10 digits), convert to ms
                if (n < 1e12) n = n * 1000;
                const d = new Date(n);
                try {
                    // Only display the date (no time) in America/New_York
                    epochDisplay = new Intl.DateTimeFormat('en-US', {
                        timeZone: 'America/New_York',
                        year: 'numeric', month: 'short', day: 'numeric'
                    }).format(d);
                } catch (e) {
                    epochDisplay = d.toDateString();
                }
            }
        }

        return { totalVectors, totalTextLength, epochDisplay };
    }, [vectors]);



    useEffect(() => {
        if (didFetchClients.current) return;
        didFetchClients.current = true;
        const fetchClients = async () => {
            const clientArray = await clientHelperService.fetchClients();
            clientArray.sort((a, b) => (a.client_name || '').localeCompare(b.client_name || ''));
            setClients(clientArray);
            if (clientArray.length > 0) setSelectedClient(clientArray[0].client_id);
        };
        fetchClients();
    }, []);

    // Warm backend vectors cache on initial page load by calling fetchVectors once.
    // We don't need the results here; this primes the server-side cache so
    // subsequent fetchAllFileSummaries calls are faster.
    useEffect(() => {
        let cancelled = false;
        const warmCache = async () => {
            try {
                await vectorService.fetchVectors();
            } catch (err) {
                if (!cancelled) console.warn('Warm cache fetchVectors failed', err);
            }
        };
        warmCache();
        return () => { cancelled = true; };
    }, []);

    // When selectedFile changes, flush any pending save for the previous file immediately
    useEffect(() => {
        const prev = prevSelectedFileRef.current;
        if (prev && prev !== selectedFile) {
            // flush pending save immediately for the previous file to avoid data loss
            flushPendingSaveImmediateFor(prev).catch(err => console.warn('Failed to flush pending save on file switch', err));
        }
        prevSelectedFileRef.current = selectedFile;
    }, [selectedFile]);

    // Load doc types list once
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                if (!docTypesLoadPromise) {
                    docTypesLoadPromise = bedrockService.getDocTypes();
                }
                const resp = await docTypesLoadPromise;
                if (cancelled) return;
                const types = resp?.doc_types || [];
                // Store full doc type objects for sub_type filtering
                setDocTypesData(types);
                // Extract name property from objects, or use string directly for backward compatibility
                const typeNames = types.map(t => typeof t === 'object' ? t.name : t).filter(Boolean);
                setDocTypesList(typeNames.length > 0 ? typeNames : DEFAULT_DOC_TYPE_OPTIONS);
            } catch (err) {
                console.warn('Failed to load doc types', err);
                setDocTypesList(DEFAULT_DOC_TYPE_OPTIONS);
            }
        };
        load();
        return () => { cancelled = true; };
    }, []);

    // Derive sub types list from docTypesData (sub_types are embedded in doc_types)
    useEffect(() => {
        if (!docTypesData || docTypesData.length === 0) return;
        
        // Extract all unique sub_types from all doc_types
        const allSubTypes = new Set();
        docTypesData.forEach(dt => {
            if (dt.sub_types && Array.isArray(dt.sub_types)) {
                dt.sub_types.forEach(st => {
                    const name = typeof st === 'object' ? st.name : st;
                    if (name) allSubTypes.add(name);
                });
            }
        });
        
        const typeNames = [...allSubTypes].filter(Boolean);
        setSubTypesList(typeNames.length > 0 ? typeNames : DEFAULT_SUB_TYPE_OPTIONS);
    }, [docTypesData]);

    // Load client type prompts for custom summary modal
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                setPromptsLoading(true);
                const response = await promptService.getPrompts();
                if (cancelled) return;

                if (response && response.prompts) {
                    // Filter prompts to only include client type prompts (owner_type === 'client')
                    // Users allowed to use app-owned prompts see the unfiltered list
                    const clientPrompts = canUseAppPrompts
                        ? response.prompts
                        : response.prompts.filter(prompt =>
                            prompt.owner_type === 'client'
                          );
                    setClientTypePrompts(clientPrompts);
                } else {
                    setClientTypePrompts([]);
                }
            } catch (error) {
                if (!cancelled) {
                    console.error('Error loading client type prompts:', error);
                    setClientTypePrompts([]);
                }
            } finally {
                if (!cancelled) {
                    setPromptsLoading(false);
                }
            }
        };
        load();
        return () => { cancelled = true; };
    }, []);

    // When the selected client changes, reset doc type and file selections
    useEffect(() => {
        setDocType('');
        setKbFiles([]);
        setSelectedFile('');
    }, [selectedClient]);

    // When client or docType changes, fetch knowledge-base files (stubbed by service)
    // Show loading for vectors summary and preview while the list of files is being fetched
    useEffect(() => {
        if (!selectedClient || !docType) {
            setKbFiles([]);
            setSelectedFile('');
            return;
        }

        let cancelled = false;
        const loadFiles = async () => {
            try {
                const files = await clientHelperService.fetchClientFiles(selectedClient, docType);
                if (!cancelled) {
                    // Normalize file objects to { name, s3_key }
                    const normalized = (files || []).map(f => ({
                        name: f.original_filename || f.stored_filename || f.name || f.filename || f.originalFilename || '',
                        s3_key: f.s3_key || f.s3Key || f.s3_key || f.stored_filename || f.local_path || ''
                    }));
                    setKbFiles(normalized);
                    const firstKey = normalized?.[0]?.s3_key || '';
                    setSelectedFile(firstKey);
                }
            } catch (err) {
                console.error('Failed to load KB files (stub)', err);
                if (!cancelled) {
                    setKbFiles([]);
                }
            }
        };
        loadFiles();
        return () => { cancelled = true; };
    }, [selectedClient, docType]);

    // Compute summaries for all kbFiles (date, total vectors, total text length)
    useEffect(() => {
        let cancelled = false;
        
        // Reset pagination when dependencies change
        setFileSummariesPagination(prev => ({ ...prev, offset: 0 }));
        
        const mapFileResponse = (f) => ({
            name: f.name || getFilenameFromUri(f.s3_key || ''),
            s3_key: f.s3_key,
            file_id: f.file_id || '',
            doc_type: f.doc_type || 'Mixed',
            sub_type: f.sub_type || 'Mixed',
            account_number: f.account_number || '',
            dateDisplay: f.dateDisplay || 'N/A',
            dateEpochMs: f.dateEpochMs || null,
            totalVectors: typeof f.totalVectors === 'number' ? f.totalVectors : 0,
            totalTextLength: typeof f.totalTextLength === 'number' ? f.totalTextLength : 0,
        });
        
        const loadSummaries = async () => {
            if (!kbFiles || kbFiles.length === 0) {
                setKbFilesSummary([]);
                setFileSummariesPagination(prev => ({ ...prev, totalCount: 0, hasMore: false }));
                return;
            }
            setLoadingKbFilesSummary(true);
            try {
                // Fetch all pages of file summaries for the selected client
                const pageSize = fileSummariesPagination.limit;
                let allFiles = [];
                let currentOffset = 0;
                let totalCount = 0;
                let hasMore = true;
                
                while (hasMore && !cancelled) {
                    const resp = await vectorService.fetchAllFileSummaries(
                        selectedClient, 
                        docType, 
                        pageSize, 
                        currentOffset
                    );
                    
                    if (!resp || !resp.success || !Array.isArray(resp.files)) {
                        break;
                    }
                    
                    const mapped = resp.files.map(mapFileResponse);
                    allFiles = [...allFiles, ...mapped];
                    totalCount = resp.total_count || 0;
                    currentOffset += pageSize;
                    hasMore = currentOffset < totalCount;
                }
                
                if (!cancelled) {
                    setKbFilesSummary(allFiles);
                    setFileSummariesPagination(prev => ({
                        ...prev,
                        offset: currentOffset,
                        totalCount: totalCount,
                        hasMore: false // All files loaded
                    }));
                    setLoadingKbFilesSummary(false);
                    return;
                }
            } catch (err) {
                // fall back to per-file fetches if the aggregated endpoint isn't available or fails
                console.warn('Aggregated file summary endpoint failed:', err);
            }
            setLoadingKbFilesSummary(false);
        };
        loadSummaries();
        return () => { cancelled = true; };
    }, [kbFiles, selectedClient, docType]);

    // When the selected file changes, fetch the file inspector attributes and also load vectors
    useEffect(() => {
        // Blank out the preview (vectors table) immediately when selecting a new file
        setVectors([]);
        setManualReviews({});
        setSelectedAnalysis(null);
        setShowAnalysisModal(false);
        setShowTextModal(false);

        if (!selectedFile) {
            setFileAttributes(defaultFileAttributes());
            setVectorPurpose('');
            setDocumentPurpose('');
            setVectorsPagination(prev => ({ ...prev, offset: 0, totalCount: 0, hasMore: false }));
            return;
        }

        let cancelled = false;
        
        // Reset pagination and load initial data
        setVectorsPagination(prev => ({ ...prev, offset: 0 }));
        loadVectorsForFile(selectedFile, true);

        // Load file attributes
        const loadFileAttributes = async () => {
            try {
                const fileResp = await vectorService.getFile(selectedFile);
                if (!cancelled && fileResp?.success && fileResp?.file) {
                    const attrs = normalizeFileFromApi(fileResp.file);
                    setFileAttributes(attrs);
                    setVectorPurpose(attrs.vector_purpose || '');
                    setDocumentPurpose(attrs.file_purpose || '');
                } else {
                    setFileAttributes(defaultFileAttributes());
                    setVectorPurpose('');
                    setDocumentPurpose('');
                }
            } catch (err) {
                if (!cancelled) {
                    console.warn('Failed to load file attributes:', err);
                    setFileAttributes(defaultFileAttributes());
                    setVectorPurpose('');
                    setDocumentPurpose('');
                }
            }
        };
        
        loadFileAttributes();

        return () => { cancelled = true; };
    }, [selectedFile]);

    // Sync vectors with detailsVectors for the modal
    useEffect(() => {
        setDetailsVectors(vectors);
    }, [vectors]);

    // Function to load vectors with pagination
    const loadVectorsForFile = async (s3Key, isInitialLoad = false, loadMore = false, fileId = null) => {
        if (!s3Key && !fileId) return;
        
        const currentOffset = isInitialLoad ? 0 : vectorsPagination.offset;
        const nextOffset = loadMore ? currentOffset + vectorsPagination.limit : currentOffset;
        
        try {
            if (loadMore) {
                setLoadingMoreVectors(true);
            }
            
            const vecResp = await vectorService.fetchVectors(s3Key, vectorsPagination.limit, nextOffset, fileId);
            
            if (vecResp?.success && Array.isArray(vecResp.vectors)) {
                const newVectors = vecResp.vectors;
                const totalCount = vecResp.total_count || 0;
                const hasMore = (nextOffset + vectorsPagination.limit) < totalCount;
                
                if (loadMore) {
                    // Append new vectors to existing ones
                    setVectors(prev => [...prev, ...newVectors]);
                } else {
                    // Replace vectors (initial load or refresh)
                    setVectors(newVectors);
                }
                
                setVectorsPagination(prev => ({
                    ...prev,
                    offset: nextOffset,
                    totalCount: totalCount,
                    hasMore: hasMore
                }));
                
                // Enqueue unanalyzed vectors for sequential analysis
                if (Array.isArray(newVectors) && newVectors.length > 0) {
                    if (isInitialLoad) {
                        analysisQueueRef.current = [];
                        analysisActiveFileRef.current = s3Key;
                    }
                    
                    for (const v of newVectors) {
                        const already = (analysisResults && analysisResults[v.key]) || v.analysis;
                        if (!already && v.key && v.text) {
                            analysisQueueRef.current.push(v);
                            setAnalyzingVectors(prev => new Set([...prev, v.key]));
                        }
                    }
                    
                    if (isInitialLoad) {
                        processAnalysisQueue().catch(err => console.warn('Failed to start analysis queue', err));
                    }
                }
            }
        } catch (err) {
            console.warn('Failed to load vectors:', err);
        } finally {
            if (loadMore) {
                setLoadingMoreVectors(false);
            }
        }
    };

    // Function to load more vectors
    const handleLoadMoreVectors = () => {
        if (selectedFile && vectorsPagination.hasMore && !loadingMoreVectors) {
            loadVectorsForFile(selectedFile, false, true, selectedFileId || null);
        }
    };

    // Function to load more file summaries  
    const handleLoadMoreFileSummaries = async () => {
        if (!fileSummariesPagination.hasMore || loadingMoreFileSummaries) return;
        
        setLoadingMoreFileSummaries(true);
        try {
            const nextOffset = fileSummariesPagination.offset + fileSummariesPagination.limit;
            const resp = await vectorService.fetchAllFileSummaries(
                selectedClient, 
                docType, 
                fileSummariesPagination.limit, 
                nextOffset
            );
            
            if (resp?.success && Array.isArray(resp.files)) {
                const mapped = resp.files.map(f => ({
                    name: f.name || getFilenameFromUri(f.s3_key || ''),
                    s3_key: f.s3_key,
                    doc_type: f.doc_type || 'Mixed',
                    sub_type: f.sub_type || 'Mixed',
                    account_number: f.account_number || '',
                    dateDisplay: f.dateDisplay || 'N/A',
                    dateEpochMs: f.dateEpochMs || null,
                    totalVectors: typeof f.totalVectors === 'number' ? f.totalVectors : 0,
                    totalTextLength: typeof f.totalTextLength === 'number' ? f.totalTextLength : 0,
                }));
                
                const totalCount = resp.total_count || 0;
                const hasMore = (nextOffset + fileSummariesPagination.limit) < totalCount;
                
                // Append new summaries to existing ones
                setKbFilesSummary(prev => [...prev, ...mapped]);
                setFileSummariesPagination(prev => ({
                    ...prev,
                    offset: nextOffset,
                    hasMore: hasMore
                }));
            }
        } catch (err) {
            console.warn('Failed to load more file summaries:', err);
        } finally {
            setLoadingMoreFileSummaries(false);
        }
    };

    const handleAnalyzeVector = async (vector) => {
        // Enqueue vector for sequential analysis
        if (!vector || !vector.key || !vector.text) {
            console.warn('Attempted to enqueue invalid vector for analysis', vector);
            return;
        }

        // If the selected file changed since queue start, reset queue for new file
        const activeFile = analysisActiveFileRef.current;
        if (activeFile && activeFile !== selectedFile) {
            // clear any existing queue/in-flight markers for old file
            analysisQueueRef.current = [];
            analysisInFlightRef.current = false;
            analysisActiveFileRef.current = null;
        }

        // initialize active file if not set
        if (!analysisActiveFileRef.current && selectedFile) analysisActiveFileRef.current = selectedFile;

        // avoid enqueueing duplicates or already-analyzed vectors
        const already = analysisResults[vector.key] || vector.analysis;
        if (already) return;
        const queued = analysisQueueRef.current.find(q => q.key === vector.key);
        if (queued) return;

        analysisQueueRef.current.push(vector);
        // optimistic UI: mark as analyzing
        setAnalyzingVectors(prev => new Set([...prev, vector.key]));

        // kick the processor
        processAnalysisQueue().catch(err => console.warn('Analysis queue error', err));
    };

    // Process the analysis queue sequentially
    const processAnalysisQueue = async () => {
        // Claim the processor upfront to avoid races where multiple callers start processing
        if (analysisInFlightRef.current) return;

        // Determine which file the queue is currently active for. Prefer the explicit
        // analysisActiveFileRef (set by the caller) because callers sometimes set
        // selectedFile state immediately before starting the processor which can
        // result in a stale selectedFile value inside this closure.
        const activeFile = analysisActiveFileRef.current || selectedFile;
        if (!activeFile) {
            // nothing to process or no file context
            analysisQueueRef.current = [];
            analysisActiveFileRef.current = null;
            return;
        }

        analysisInFlightRef.current = true;
        try {
            // ensure the active file ref is initialized
            analysisActiveFileRef.current = analysisActiveFileRef.current || selectedFile;

            // capture the file we expect to process so we can detect mid-run changes
            const processingFile = analysisActiveFileRef.current;

            while (analysisQueueRef.current.length > 0) {
                const next = analysisQueueRef.current.shift();
                if (!next || !next.key || !next.text) continue;

                // If the active file was changed by another actor while we were running,
                // re-queue this item and stop processing to allow the new owner to take over.
                if (analysisActiveFileRef.current !== processingFile) {
                    analysisQueueRef.current.unshift(next);
                    break;
                }

                try {
                    const result = await vectorService.analyzeVector(next.key, next.text);
                    if (result && result.success && result.analysis) {
                        setAnalysisResults(prev => ({ ...prev, [next.key]: result.analysis }));
                    } else {
                        console.warn('Analysis failed for vector', next.key, result?.error);
                    }
                } catch (err) {
                    console.error('Analysis error for vector', next.key, err);
                } finally {
                    // remove analyzing state for this vector
                    setAnalyzingVectors(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(next.key);
                        return newSet;
                    });
                }
                // small, non-blocking delay could be added here if desired to further throttle
            }
        } finally {
            analysisInFlightRef.current = false;
            // queue drained or file changed
            if (analysisQueueRef.current.length === 0) {
                analysisActiveFileRef.current = null;
            }
        }
    };

    const handleAnalyzeVectorsPurpose = async () => {
        // collect all "what" values from analysisResults or embedded vector.analysis
        const whats = (detailsVectors || []).map(v => {
            const a = analysisResults[v.key] || v.analysis || {};
            return a.what || '';
        }).filter(Boolean);

        if (!whats || whats.length === 0) {
            alert('No analysis "what" values available from vectors to send');
            return;
        }

        setAnalyzingVectorPurpose(true);
        try {
            const resp = await vectorService.getVectorsPurpose(whats);
            if (resp && resp.success) {
                const purpose = resp.purpose || resp.result || JSON.stringify(resp);
                setVectorPurpose(purpose);
                // Persist the computed vector purpose to the file attributes
                if (selectedFile) {
                    try {
                        // Merge existing attributes with the new vector purpose so we don't wipe other fields
                        const mergedAttrs = { ...(fileAttributes || {}), vector_purpose: purpose };
                        markDirty(mergedAttrs);
                    } catch (saveErr) {
                        console.warn('Failed to persist vectors purpose to file attributes', saveErr);
                    }
                }
            } else {
                alert(`Failed to get vectors purpose: ${resp?.error || 'Unknown error'}`);
            }
        } catch (err) {
            console.error('Error fetching vectors purpose', err);
            alert('Error fetching vectors purpose. See console for details.');
        } finally {
            setAnalyzingVectorPurpose(false);
        }
    };

    const handleAnalyzeDocumentPurpose = async () => {
        if (!selectedFile) {
            alert('No file selected (S3 key) to analyze');
            return;
        }
        setAnalyzingDocumentPurpose(true);
        try {
            const resp = await vectorService.getDocumentPurpose(selectedFile);
            if (resp && resp.success) {
                // Check if this is an async job response
                if (resp.job_id) {
                    // Poll for completion
                    const pollInterval = 2000; // Poll every 2 seconds
                    const maxAttempts = 150; // Max 5 minutes
                    
                    for (let attempt = 0; attempt < maxAttempts; attempt++) {
                        await new Promise(resolve => setTimeout(resolve, pollInterval));
                        
                        try {
                            const statusResp = await vectorService.pollDocumentPurposeJob(resp.job_id);
                            
                            if (statusResp.status === 'completed') {
                                const purpose = statusResp.summary || statusResp.purpose || '';
                                setDocumentPurpose(purpose);
                                
                                // Persist document (file) purpose to file attributes
                                if (selectedFile && purpose) {
                                    try {
                                        // Merge existing attributes with the new file purpose to avoid overwriting
                                        const mergedAttrs = { ...(fileAttributes || {}), file_purpose: purpose };
                                        markDirty(mergedAttrs);
                                    } catch (saveErr) {
                                        console.warn('Failed to persist document purpose to file attributes', saveErr);
                                    }
                                }
                                break;
                            } else if (statusResp.status === 'failed') {
                                console.error('Document purpose job failed:', statusResp.error);
                                alert(`Failed to analyze document purpose: ${statusResp.error || 'Unknown error'}`);
                                break;
                            }
                        } catch (pollError) {
                            console.error('Error polling document purpose job:', pollError);
                        }
                    }
                } else {
                    // Synchronous response (backwards compatibility)
                    const purpose = resp.purpose || resp.result || JSON.stringify(resp);
                    setDocumentPurpose(purpose);
                    
                    // Persist document (file) purpose to file attributes
                    if (selectedFile) {
                        try {
                            // Merge existing attributes with the new file purpose to avoid overwriting
                            const mergedAttrs = { ...(fileAttributes || {}), file_purpose: purpose };
                            markDirty(mergedAttrs);
                        } catch (saveErr) {
                            console.warn('Failed to persist document purpose to file attributes', saveErr);
                        }
                    }
                }
            } else {
                alert(`Failed to get document purpose: ${resp?.error || 'Unknown error'}`);
            }
        } catch (err) {
            console.error('Error fetching document purpose', err);
            alert('Error fetching document purpose. See console for details.');
        } finally {
            setAnalyzingDocumentPurpose(false);
        }
    };

    const getCategoryBadgeClass = (category) => {
        switch (category?.toLowerCase()) {
            case 'signal': return 'badge bg-success';
            case 'noise': return 'badge bg-danger';
            case 'maybe': return 'badge bg-warning';
            default: return 'badge bg-secondary';
        }
    };

    // Toggle acceptance for vector or file purpose and persist immediately
    const handleToggleAccepted = async (which, checked) => {
        if (!selectedFile) return;
        const key = which === 'vector' ? 'vector_purpose_accepted' : 'file_purpose_accepted';
        // Build merged attributes from the latest known fileAttributes to avoid stale closures
        const mergedAttrs = { ...(fileAttributes || {}), [key]: !!checked };
        const finalAttrs = { ...mergedAttrs };
        // Optimistically update UI
        setFileAttributes(finalAttrs);
        try {
            markDirty(finalAttrs);
        } catch (err) {
            console.warn('Failed to mark accepted flag dirty', err);
        }
    };

    // Set or unset the best purpose ('vector' or 'file'). Only one can be set at a time.
    const handleSetBestPurpose = async (which) => {
        if (!selectedFile) return;
        const currentBest = fileAttributes && fileAttributes.best_purpose ? fileAttributes.best_purpose : '';
        const newVal = currentBest === which ? '' : which;
        // Build merged attrs from latest state
        const mergedAttrs = { ...(fileAttributes || {}), best_purpose: newVal };
        const finalAttrs = { ...mergedAttrs };
        // optimistic UI update
        setFileAttributes(finalAttrs);
        try {
            markDirty(finalAttrs);
        } catch (err) {
            console.warn('Failed to mark best purpose dirty', err);
        }
    };

    const showAnalysisDetails = (vectorKey, analysis) => {
        setSelectedAnalysis({ vectorKey, ...analysis });
        setShowAnalysisModal(true);
    };

    const showTextDetails = (text, vectorKey) => {
        setSelectedTextFull(text || '');
        setSelectedTextKey(vectorKey || '');
        setShowTextModal(true);
    };

    // Review modal state
    const [reviewModalOpen, setReviewModalOpen] = useState(false);
    const [reviewVectorKey, setReviewVectorKey] = useState(null);
    const [reviewForm, setReviewForm] = useState({ thinking: '', what: '', category: '', score: '', why: '' });
    // Details modal for per-file view (Vectors Summary + Preview)
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [detailsFileKey, setDetailsFileKey] = useState(null);
    const [detailsVectors, setDetailsVectors] = useState([]);
    const [detailsFileAttributes, setDetailsFileAttributes] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const detailsPrevRef = useRef(null);

    // Summary modal for document preview
    const [summaryModalOpen, setSummaryModalOpen] = useState(false);
    const [summaryFileKey, setSummaryFileKey] = useState(null);
    const [summaryFileAttributes, setSummaryFileAttributes] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [summaryContent, setSummaryContent] = useState(null);
    const [summaryError, setSummaryError] = useState(null);
    const [summaryContentLoading, setSummaryContentLoading] = useState(false);

    // Document summary state
    const [documentSummary, setDocumentSummary] = useState('');
    const [originalDocumentSummary, setOriginalDocumentSummary] = useState(''); // Store original AI-generated summary
    const [editedDocumentSummary, setEditedDocumentSummary] = useState(''); // Store edited summary with annotations
    const [documentSummaryLoading, setDocumentSummaryLoading] = useState(false);
    const [isCustomSummary, setIsCustomSummary] = useState(false); // Track if current summary is custom
    const [triggerSummaryEdit, setTriggerSummaryEdit] = useState(false); // Trigger edit mode in SummaryEditor
    const [summaryViewState, setSummaryViewState] = useState({ isEditing: false, showingOriginal: false }); // Track SummaryEditor view state

    // Custom summary modal state
    const [customSummaryModalOpen, setCustomSummaryModalOpen] = useState(false);
    const [customSummaryPrompt, setCustomSummaryPrompt] = useState('');
    const [wordWrap, setWordWrap] = useState(true);

    // Client type prompts state for custom summary modal
    const [clientTypePrompts, setClientTypePrompts] = useState([]);
    const [selectedClientPrompt, setSelectedClientPrompt] = useState('');
    const [selectedPromptSystemPrompt, setSelectedPromptSystemPrompt] = useState('');
    const [selectedPromptPrompt, setSelectedPromptPrompt] = useState('');
    const [promptsLoading, setPromptsLoading] = useState(false);

    // Enable the 'Analyze vectors purpose' button only when every vector
    // has an analysis 'what' (either from server, analysisResults state, or manual review).
    const canAnalyzeVectorsPurpose = React.useMemo(() => {
        if (!Array.isArray(detailsVectors) || detailsVectors.length === 0) return false;
        let count = 0;
        for (const v of detailsVectors) {
            const a = (analysisResults && analysisResults[v.key]) || v.analysis || {};
            if (a && (a.what && String(a.what).trim().length > 0)) count += 1;
        }
        return count === detailsVectors.length;
    }, [detailsVectors, analysisResults]);

    const openReviewModal = (vector) => {
        // Prefill with existing manual review first, then local analysis, then server analysis
        const existing = manualReviews[vector.key] || analysisResults[vector.key] || vector.analysis || {};
        setReviewForm({
            thinking: existing.thinking || '',
            what: existing.what || '',
            category: existing.category || '',
            score: existing.score != null ? String(existing.score) : '',
            why: existing.why || ''
        });
        setReviewVectorKey(vector.key);
        setReviewModalOpen(true);
    };

    const openSummaryModal = async (s3Key) => {
        if (!s3Key) return;
        setSummaryLoading(true);
        setSummaryFileKey(s3Key);
        setSummaryFileAttributes(null);
        setSummaryContent(null);
        setSummaryError(null);
        setDocumentSummary('');
        setOriginalDocumentSummary('');
        setEditedDocumentSummary('');
        setSummaryModalOpen(true);

        try {
            // Fetch file attributes
            const fileResp = await vectorService.getFile(s3Key);
            const attrs = (fileResp && fileResp.success && fileResp.file) ? normalizeFileFromApi(fileResp.file) : defaultFileAttributes();
            setSummaryFileAttributes(attrs);

            // Fetch file content for preview
            setSummaryContentLoading(true);
            try {
                const content = await fetchFileContent(s3Key);
                setSummaryContent(content);
            } catch (err) {
                console.error('Failed to fetch file content for summary', err);
                setSummaryError(err.message || 'Failed to load file content');
            } finally {
                setSummaryContentLoading(false);
            }

            // Load existing document summary from attributes or fetch new one
            setDocumentSummaryLoading(true);
            try {
                // Check if we already have a saved summary in attributes
                if (attrs && attrs.doc_summary) {
                    setDocumentSummary(attrs.doc_summary);
                    setOriginalDocumentSummary(attrs.doc_summary);
                    setEditedDocumentSummary(attrs.edited_document_summary || '');
                    setIsCustomSummary(false); // This is a saved default summary
                    setDocumentSummaryLoading(false);
                } else {
                    // Fetch document summary - now returns job_id for polling
                    const summaryResp = await vectorService.getDocumentSummary(s3Key, attrs.category);
                    if (summaryResp && summaryResp.success) {
                        // Check if this is an async job response
                        if (summaryResp.job_id) {
                            // Poll for completion
                            const pollInterval = 2000; // Poll every 2 seconds
                            const maxAttempts = 150; // Max 5 minutes (150 * 2 seconds)
                            
                            for (let attempt = 0; attempt < maxAttempts; attempt++) {
                                await new Promise(resolve => setTimeout(resolve, pollInterval));
                                
                                try {
                                    const statusResp = await vectorService.pollDocumentSummaryJob(summaryResp.job_id);
                                    
                                    if (statusResp.status === 'completed') {
                                        // Job completed successfully
                                        const newSummary = statusResp.summary || '';
                                        setDocumentSummary(newSummary);
                                        setOriginalDocumentSummary(newSummary);
                                        setIsCustomSummary(false);
                                        
                                        // Save the default summary to attributes
                                        if (newSummary && !attrs.doc_summary) {
                                            const updatedAttrs = { ...attrs, doc_summary: newSummary };
                                            setSummaryFileAttributes(updatedAttrs);
                                            try {
                                                await vectorService.updateFile(s3Key, updatedAttrs);
                                            } catch (saveErr) {
                                                console.warn('Failed to save document summary to attributes:', saveErr);
                                            }
                                        }
                                        break;
                                    } else if (statusResp.status === 'failed') {
                                        console.error('Document summary job failed:', statusResp.error);
                                        alert(`Failed to generate document summary: ${statusResp.error || 'Unknown error'}`);
                                        break;
                                    }
                                    // Continue polling if status is pending/processing
                                } catch (pollError) {
                                    console.error('Error polling document summary job:', pollError);
                                    // Continue polling on error
                                }
                            }
                        } else {
                            // Synchronous response (backwards compatibility)
                            const newSummary = summaryResp.summary || summaryResp.purpose || '';
                            setDocumentSummary(newSummary);
                            setOriginalDocumentSummary(newSummary);
                            setIsCustomSummary(false);
                            
                            // Save the default summary to attributes
                            if (newSummary && !attrs.doc_summary) {
                                const updatedAttrs = { ...attrs, doc_summary: newSummary };
                                setSummaryFileAttributes(updatedAttrs);
                                try {
                                    await vectorService.updateFile(s3Key, updatedAttrs);
                                } catch (saveErr) {
                                    console.warn('Failed to save document summary to attributes:', saveErr);
                                }
                            }
                        }
                    } else {
                        console.warn('Failed to fetch document summary:', summaryResp?.error);
                    }
                    setDocumentSummaryLoading(false);
                }
            } catch (err) {
                console.error('Failed to fetch document summary', err);
                setDocumentSummaryLoading(false);
            }
        } catch (err) {
            console.error('Failed to open summary modal', err);
            setSummaryFileAttributes(defaultFileAttributes());
        } finally {
            setSummaryLoading(false);
        }
    };

    const closeSummaryModal = async () => {
        // Save edited summary before closing if it exists and we have file info
        if (summaryFileKey && editedDocumentSummary && editedDocumentSummary.trim() && summaryFileAttributes) {
            try {
                console.log('Saving edited summary on modal close:', { 
                    s3Key: summaryFileKey, 
                    editedSummary: editedDocumentSummary.substring(0, 100) + '...' 
                });

                const updateResult = await vectorService.updateFile(summaryFileKey, {
                    edited_document_summary: editedDocumentSummary
                });

                if (updateResult.success) {
                    console.log('Successfully saved edited summary');
                } else {
                    console.error('Failed to save edited summary:', updateResult);
                }
            } catch (error) {
                console.error('Error saving edited summary:', error);
            }
        }

        setSummaryModalOpen(false);
        setSummaryFileKey(null);
        setSummaryFileAttributes(null);
        setSummaryContent(null);
        setSummaryError(null);
        setSummaryLoading(false);
        setSummaryContentLoading(false);
        setDocumentSummary('');
        setOriginalDocumentSummary('');
        setEditedDocumentSummary('');
        setDocumentSummaryLoading(false);
        setIsCustomSummary(false); // Reset custom summary flag
        setTriggerSummaryEdit(false); // Reset edit trigger
    };

    const handleExportSummaryToPdf = async () => {
        try {
            // Determine which summary to export based on current view state
            let summaryToExport;
            if (summaryViewState.isEditing) {
                // Currently editing - export the edited version (already HTML)
                summaryToExport = editedDocumentSummary || documentSummary;
            } else if (summaryViewState.showingOriginal) {
                // Viewing original - export original (convert markdown to HTML)
                const originalContent = originalDocumentSummary || documentSummary;
                summaryToExport = textToHtml(originalContent);
            } else {
                // Viewing edited (or no edits exist) - export edited or current
                // If edited exists, it's HTML; if not, convert markdown to HTML
                if (editedDocumentSummary) {
                    summaryToExport = editedDocumentSummary;
                } else {
                    summaryToExport = textToHtml(documentSummary);
                }
            }
            
            if (!summaryToExport || !summaryFileKey) {
                alert('No summary available to export');
                return;
            }

            // Get filename from s3 path
            const filename = getFilenameFromUri(summaryFileKey);
            
            // Call vector service to generate PDF
            const blob = await vectorService.exportSummaryToPdf(summaryFileKey, summaryToExport, filename);
            
            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const baseName = filename.split('.').slice(0, -1).join('.') || 'document';
            link.download = `${baseName}_summary.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
        } catch (error) {
            console.error('Error exporting summary to PDF:', error);
            alert('Failed to export summary to PDF. Please try again.');
        }
    };

    const handleRegenerateDocumentSummary = async () => {
        if (!summaryFileKey) return;

        setDocumentSummaryLoading(true);
        try {
            const summaryResp = await vectorService.getDocumentSummary(
                summaryFileKey,
                summaryFileAttributes?.category
            );
            if (summaryResp && summaryResp.success) {
                // Check if this is an async job response
                if (summaryResp.job_id) {
                    // Poll for completion
                    const pollInterval = 2000; // Poll every 2 seconds
                    const maxAttempts = 150; // Max 5 minutes
                    
                    for (let attempt = 0; attempt < maxAttempts; attempt++) {
                        await new Promise(resolve => setTimeout(resolve, pollInterval));
                        
                        try {
                            const statusResp = await vectorService.pollDocumentSummaryJob(summaryResp.job_id);
                            
                            if (statusResp.status === 'completed') {
                                const newSummary = statusResp.summary || '';
                                setDocumentSummary(newSummary);
                                setOriginalDocumentSummary(newSummary);
                                setEditedDocumentSummary(''); // Clear edits on regenerate
                                setIsCustomSummary(false);
                                
                                // Save the new default summary to attributes
                                if (newSummary) {
                                    const updatedAttrs = { ...summaryFileAttributes, doc_summary: newSummary };
                                    setSummaryFileAttributes(updatedAttrs);
                                    try {
                                        await vectorService.updateFile(summaryFileKey, updatedAttrs);
                                    } catch (saveErr) {
                                        console.warn('Failed to save regenerated document summary:', saveErr);
                                    }
                                }
                                break;
                            } else if (statusResp.status === 'failed') {
                                console.error('Document summary job failed:', statusResp.error);
                                alert(`Failed to regenerate document summary: ${statusResp.error || 'Unknown error'}`);
                                break;
                            }
                        } catch (pollError) {
                            console.error('Error polling document summary job:', pollError);
                        }
                    }
                } else {
                    // Synchronous response (backwards compatibility)
                    const newSummary = summaryResp.summary || summaryResp.purpose || '';
                    setDocumentSummary(newSummary);
                    setOriginalDocumentSummary(newSummary);
                    setEditedDocumentSummary(''); // Clear edits on regenerate
                    setIsCustomSummary(false);
                    
                    // Save the new default summary to attributes
                    if (newSummary) {
                        const updatedAttrs = { ...summaryFileAttributes, doc_summary: newSummary };
                        setSummaryFileAttributes(updatedAttrs);
                        try {
                            await vectorService.updateFile(summaryFileKey, updatedAttrs);
                        } catch (saveErr) {
                            console.warn('Failed to save regenerated document summary:', saveErr);
                        }
                    }
                }
            } else {
                console.warn('Failed to regenerate document summary:', summaryResp?.error);
                alert('Failed to regenerate document summary. Please try again.');
            }
        } catch (err) {
            console.error('Failed to regenerate document summary', err);
            alert('Error regenerating document summary. See console for details.');
        } finally {
            setDocumentSummaryLoading(false);
        }
    };

    const handleOpenCustomSummaryModal = () => {
        setCustomSummaryPrompt(''); // Reset prompt
        setSelectedClientPrompt('');
        setSelectedPromptSystemPrompt('');
        setSelectedPromptPrompt('');
        setCustomSummaryModalOpen(true);
    };

    const handleCloseCustomSummaryModal = () => {
        setCustomSummaryModalOpen(false);
        setCustomSummaryPrompt('');
        setSelectedClientPrompt('');
        setSelectedPromptSystemPrompt('');
        setSelectedPromptPrompt('');
    };

    const handleSubmitCustomSummary = async () => {
        if (!summaryFileKey) return;

        let promptToUse = '';

        if (selectedClientPrompt) {
            // Use the selected client prompt's content
            if (!selectedPromptSystemPrompt.trim() && !selectedPromptPrompt.trim()) {
                alert('Please enter system prompt and/or prompt text.');
                return;
            }

            // Combine system prompt and prompt with clear delineation
            const systemPart = selectedPromptSystemPrompt.trim() ? `System: ${selectedPromptSystemPrompt.trim()}\n\n` : '';
            const userPart = selectedPromptPrompt.trim() ? `User: ${selectedPromptPrompt.trim()}` : '';
            promptToUse = systemPart + userPart;
        } else {
            // Use the custom text area prompt
            if (!customSummaryPrompt.trim()) {
                alert('Please enter a custom prompt.');
                return;
            }
            promptToUse = customSummaryPrompt.trim();
        }

        setDocumentSummaryLoading(true);
        setCustomSummaryModalOpen(false); // Close modal immediately

        try {
            const summaryResp = await vectorService.getDocumentSummary(
                summaryFileKey,
                summaryFileAttributes?.category,
                promptToUse // Use the determined prompt
            );
            if (summaryResp && summaryResp.success) {
                // Check if this is an async job response
                if (summaryResp.job_id) {
                    // Poll for completion
                    const pollInterval = 2000; // Poll every 2 seconds
                    const maxAttempts = 150; // Max 5 minutes
                    
                    for (let attempt = 0; attempt < maxAttempts; attempt++) {
                        await new Promise(resolve => setTimeout(resolve, pollInterval));
                        
                        try {
                            const statusResp = await vectorService.pollDocumentSummaryJob(summaryResp.job_id);
                            
                            if (statusResp.status === 'completed') {
                                // Set the custom summary for display only (don't save to database)
                                setDocumentSummary(statusResp.summary || '');
                                setOriginalDocumentSummary(originalDocumentSummary || statusResp.summary || ''); // Preserve original if exists
                                setEditedDocumentSummary(''); // Clear edits on custom summary
                                setIsCustomSummary(true); // Mark this as a custom summary
                                // Note: We intentionally do NOT save custom summaries to the database
                                break;
                            } else if (statusResp.status === 'failed') {
                                console.error('Custom document summary job failed:', statusResp.error);
                                alert(`Failed to generate custom document summary: ${statusResp.error || 'Unknown error'}`);
                                break;
                            }
                        } catch (pollError) {
                            console.error('Error polling custom document summary job:', pollError);
                        }
                    }
                } else {
                    // Synchronous response (backwards compatibility)
                    // Set the custom summary for display only (don't save to database)
                    setDocumentSummary(summaryResp.summary || summaryResp.purpose || '');
                    setOriginalDocumentSummary(originalDocumentSummary || summaryResp.summary || summaryResp.purpose || '');
                    setEditedDocumentSummary(''); // Clear edits on custom summary
                    setIsCustomSummary(true); // Mark this as a custom summary
                    // Note: We intentionally do NOT save custom summaries to the database
                }
            } else {
                console.warn('Failed to generate custom document summary:', summaryResp?.error);
                alert('Failed to generate custom document summary. Please try again.');
            }
        } catch (err) {
            console.error('Failed to generate custom document summary', err);
            alert('Error generating custom document summary. See console for details.');
        } finally {
            setDocumentSummaryLoading(false);
        }
    };

    const openDetailsModal = async (s3Key, fileId = null) => {
        if (!s3Key) return;
        setDetailsLoading(true);
        setDetailsFileKey(s3Key);
        setDetailsVectors([]);
        setDetailsFileAttributes(null);
        // Open modal immediately and show spinner while content loads
        setDetailsModalOpen(true);
        // save previous selection so we can restore on close
        detailsPrevRef.current = {
            selectedFile: selectedFile,
            selectedFileId: selectedFileId,
            fileAttributes: fileAttributes,
            vectors: vectors,
            vectorPurpose: vectorPurpose,
            documentPurpose: documentPurpose
        };
        try {
            // set global selection so modal controls reuse existing handlers
            setSelectedFile(s3Key);
            setSelectedFileId(fileId || '');
            
            // Reset pagination for new file
            setVectorsPagination(prev => ({ ...prev, offset: 0 }));
            
            // Load file attributes and vectors with pagination
            const [fileResp] = await Promise.allSettled([vectorService.getFile(s3Key)]);
            const attrs = (fileResp.status === 'fulfilled' && fileResp.value && fileResp.value.success && fileResp.value.file) ? normalizeFileFromApi(fileResp.value.file) : defaultFileAttributes();
            
            setFileAttributes(attrs);
            setVectorPurpose(attrs.vector_purpose || '');
            setDocumentPurpose(attrs.file_purpose || '');
            setDetailsFileAttributes(attrs);
            
            // Load vectors using pagination function - this will update both vectors and detailsVectors
            await loadVectorsForFile(s3Key, true, false, fileId);
        } catch (err) {
            console.error('Failed to open details modal', err);
            const def = defaultFileAttributes();
            setSelectedFile(s3Key);
            setFileAttributes(def);
            setVectors([]);
            setVectorPurpose('');
            setDocumentPurpose('');
            setDetailsFileAttributes(def);
            setDetailsVectors([]);
            setDetailsModalOpen(true);
        } finally {
            setDetailsLoading(false);
        }
    };

    const closeDetailsModal = async () => {
        // Flush any pending saves before closing
        await flushPendingSave();
        
        // Capture the file key for the modal and prefer the live global
        // `fileAttributes`/`vectors` which reflect edits the user made while
        // the modal was open. Fall back to the snapshot `detailsFileAttributes`
        // and `detailsVectors` if the live values are not available.
        const curDetailsKey = detailsFileKey;
        const curDetailsAttrs = fileAttributes || detailsFileAttributes;
        const curDetailsVecs = vectors && vectors.length > 0 ? vectors : detailsVectors;

        // restore previous selection
        const prev = detailsPrevRef.current || {};
        setDetailsModalOpen(false);
        setDetailsFileKey(null);
        setDetailsVectors([]);
        setDetailsFileAttributes(null);
        setDetailsLoading(false);
        setSelectedFile(prev.selectedFile || '');
        setSelectedFileId(prev.selectedFileId || '');
        setFileAttributes(prev.fileAttributes || null);
        setVectors(prev.vectors || []);
        setVectorPurpose(prev.vectorPurpose || '');
        setDocumentPurpose(prev.documentPurpose || '');
        detailsPrevRef.current = null;

        // Instead of refetching the full KB files list and summaries, update the
        // existing `kbFilesSummary` entry for the file we just edited in the modal.
        // This avoids an expensive round-trip and keeps state in sync with the
        // optimistic/committed changes made in the modal.
        if (curDetailsKey) {
            setKbFilesSummary(prevSummary => {
                if (!Array.isArray(prevSummary) || prevSummary.length === 0) return prevSummary;
                const totalVectors = Array.isArray(curDetailsVecs) ? curDetailsVecs.length : undefined;
                const totalTextLength = Array.isArray(curDetailsVecs) ? curDetailsVecs.reduce((acc, v) => acc + ((v && v.text) ? v.text.length : 0), 0) : undefined;
                return prevSummary.map(f => {
                    if (!f || f.s3_key !== curDetailsKey) return f;
                    return {
                        ...f,
                        // Use the modal's attributes if available, otherwise fall back to current fileAttributes
                        doc_type: (curDetailsAttrs && curDetailsAttrs.doc_type) ? curDetailsAttrs.doc_type : (f.doc_type || 'Mixed'),
                        sub_type: (curDetailsAttrs && curDetailsAttrs.sub_type) ? curDetailsAttrs.sub_type : (f.sub_type || 'Mixed'),
                        account_number: (curDetailsAttrs && curDetailsAttrs.account_number) ? curDetailsAttrs.account_number : (f.account_number || ''),
                        totalVectors: typeof totalVectors === 'number' ? totalVectors : f.totalVectors,
                        totalTextLength: typeof totalTextLength === 'number' ? totalTextLength : f.totalTextLength,
                    };
                });
            });
        }
    };

    const submitReview = async () => {
        if (!reviewVectorKey) return;
        // basic validation
        if (!reviewForm.category) {
            alert('Please select a category');
            return;
        }
        const payload = {
            thinking: reviewForm.thinking,
            what: reviewForm.what,
            category: reviewForm.category,
            score: reviewForm.score ? Number(reviewForm.score) : null,
            why: reviewForm.why
        };
        try {
            const resp = await vectorService.submitReview(reviewVectorKey, payload);
            if (resp && resp.success) {
                // store manual review separately and update UI
                setManualReviews(prev => ({ ...prev, [reviewVectorKey]: resp.analysis }));
                // also update vectors array to include a manual_review field for persistence in current view
                setVectors(prev => prev.map(v => v.key === reviewVectorKey ? { ...v, manual_review: resp.analysis } : v));
                setReviewModalOpen(false);
            } else {
                alert('Failed to save review');
            }
        } catch (err) {
            console.error('Failed to submit review', err);
            alert('Failed to submit review. See console for details.');
        }
    };

    // Doc Type management handlers (modal)
    const refreshDocTypes = async () => {
        try {
            const resp = await bedrockService.getDocTypes();
            const types = resp?.doc_types || [];
            // Store full doc type objects for sub_type filtering
            setDocTypesData(types);
            // Extract name property from objects, or use string directly for backward compatibility
            const typeNames = types.map(t => typeof t === 'object' ? t.name : t).filter(Boolean);
            setDocTypesList(typeNames.length > 0 ? typeNames : DEFAULT_DOC_TYPE_OPTIONS);
            // Clear the cached promise so future loads get fresh data
            docTypesLoadPromise = null;
        } catch (err) {
            console.warn('Failed to refresh doc types', err);
        }
    };

    const handleAddDocTypeFromModal = async () => {
        const v = (docTypeModalInput || '').trim();
        if (!v) return;
        setDocTypeModalBusy(true);
        try {
            await bedrockService.addDocType(v);
            await refreshDocTypes();
            setDocTypeModalInput('');
        } catch (err) {
            console.error('Failed to add doc type', err);
            alert('Failed to add doc type. See console for details.');
        } finally {
            setDocTypeModalBusy(false);
        }
    };

    const handleDeleteDocType = async (dt) => {
        if (!dt) return;
        const docTypeId = typeof dt === 'object' ? dt.id : dt;
        const docTypeName = typeof dt === 'object' ? dt.name : dt;
        setDocTypeModalBusy(true);
        try {
            await bedrockService.deleteDocType(docTypeId);
            await refreshDocTypes();
            // If currently selected doc_type equals deleted one, clear it on file
            if (fileAttributes && (fileAttributes.doc_type_id === docTypeId || fileAttributes.doc_type === docTypeName)) {
                setFileAttributes(prev => ({ ...(prev || {}), doc_type: '', doc_type_id: '', sub_type: '', sub_type_id: '' }));
                try { markDirty({ ...(fileAttributes || {}), doc_type_id: '', sub_type_id: '' }); } catch (err) { /* ignore */ }
            }
        } catch (err) {
            console.error('Failed to delete doc type', err);
            alert('Failed to delete doc type. See console for details.');
        } finally {
            setDocTypeModalBusy(false);
        }
    };

    const handleStartRenameDocType = (dt) => {
        setRenamingDocType(dt);
        setRenameDocTypeInput(dt);
    };

    const handleCancelRenameDocType = () => {
        setRenamingDocType(null);
        setRenameDocTypeInput('');
    };

    const handleConfirmRenameDocType = async () => {
        const from = (renamingDocType || '').trim();
        const to = (renameDocTypeInput || '').trim();
        if (!from || !to || from === to) {
            handleCancelRenameDocType();
            return;
        }
        setDocTypeModalBusy(true);
        try {
            await bedrockService.renameDocType(from, to);
            await refreshDocTypes();
            // Update current fileAttributes display name if they referenced the old doc_type (ID stays same)
            if (fileAttributes && fileAttributes.doc_type === from) {
                setFileAttributes(prev => ({ ...(prev || {}), doc_type: to }));
                // No need to markDirty - ID stays the same, only display name changed
            }
            // Update kbFilesSummary entries optimistically
            setKbFilesSummary(prev => (Array.isArray(prev) ? prev.map(f => (f && f.doc_type === from ? { ...f, doc_type: to } : f)) : prev));
        } catch (err) {
            console.error('Failed to rename doc type', err);
            alert('Failed to rename doc type. See console for details.');
        } finally {
            setDocTypeModalBusy(false);
            handleCancelRenameDocType();
        }
    };

    // Sub Type management handlers (modal)
    // Refresh sub_types by re-fetching doc_types (sub_types are embedded in doc_types)
    const refreshSubTypes = async () => {
        try {
            // Clear cached promise to get fresh data
            docTypesLoadPromise = null;
            const resp = await bedrockService.getDocTypes();
            const types = resp?.doc_types || [];
            // Update docTypesData which will trigger the useEffect to update subTypesList
            setDocTypesData(types);
            // Also update docTypesList
            const typeNames = types.map(t => typeof t === 'object' ? t.name : t).filter(Boolean);
            setDocTypesList(typeNames.length > 0 ? typeNames : DEFAULT_DOC_TYPE_OPTIONS);
        } catch (err) {
            console.warn('Failed to refresh sub types', err);
        }
    };

    const handleAddSubTypeFromModal = async () => {
        // Sub_types are now managed per doc_type, not globally
        alert('Sub-types are now managed within each Document Type. Please edit a Document Type to add or remove sub-types.');
    };

    const handleDeleteSubType = async (st) => {
        // Sub_types are now managed per doc_type, not globally
        alert('Sub-types are now managed within each Document Type. Please edit a Document Type to add or remove sub-types.');
    };

    const handleStartRenameSubType = (st) => {
        setRenamingSubType(st);
        setRenameSubTypeInput(st);
    };

    const handleCancelRenameSubType = () => {
        setRenamingSubType(null);
        setRenameSubTypeInput('');
    };

    const handleConfirmRenameSubType = async () => {
        // Sub_types are now managed per doc_type, not globally
        alert('Sub-types are now managed within each Document Type. Please edit a Document Type to rename sub-types.');
        handleCancelRenameSubType();
    };

    // Helpers to work with S3 URIs and fetch file content (copied from ClientHelper)
    function getFilenameFromUri(uri) {
        if (!uri) return '';
        const parts = uri.split('/');
        return parts[parts.length - 1];
    }

    function getFileType(filename) {
        const ext = filename.split('.').pop().toLowerCase();
        if (["png", "jpg", "jpeg", "gif", "bmp", "webp"].includes(ext)) return "image";
        if (["txt", "csv", "log", "json", "md"].includes(ext)) return "text";
        if (["pdf"].includes(ext)) return "pdf";
        if (["doc", "docx"].includes(ext)) return "doc";
        return "other";
    }

    function getClientIdFromUri(uri) {
        if (!uri) return '';
        const parts = uri.split('/');
        return parts[parts.length - 2];
    }
    function getDocTypeFromUri(uri) {
        if (!uri) return '';
        const parts = uri.split('/');
        return parts[parts.length - 3];
    }

    async function fetchFileContent(fileUri) {
        const filename = getFilenameFromUri(fileUri);
        const client_id = getClientIdFromUri(fileUri);
        const doc_type = getDocTypeFromUri(fileUri);

        // Check if it's a DOCX file and convert to PDF for preview
        const isDocx = filename.toLowerCase().endsWith('.docx');
        const convertToPdf = isDocx;

        const { blob, headers } = await clientHelperService.downloadFile(filename, client_id, doc_type, convertToPdf);
        const contentType = blob?.type || headers['content-type'] || '';

        console.log('File download result:', { filename, contentType, blobSize: blob.size, convertToPdf });

        // Check if blob is valid and has content
        if (!blob || blob.size === 0) {
            throw new Error('File not found or empty');
        }

        // Determine file type from filename extension
        const fileType = getFileType(filename);

        // For text files, read the blob content directly instead of creating a blob URL
        // Check both content type and file extension to handle cases where content-type is generic
        if (contentType.startsWith('text/') || contentType.includes('json') || 
            fileType === 'text' || filename.toLowerCase().endsWith('.json')) {
            return await blob.text();
        } else if (contentType.startsWith('image/') || fileType === 'image') {
            return URL.createObjectURL(blob);
        } else if (contentType === 'application/pdf' || filename.toLowerCase().endsWith('.pdf') || convertToPdf) {
            // Create blob URL for PDF with explicit type
            const pdfBlob = new Blob([blob], { type: 'application/pdf' });
            const url = URL.createObjectURL(pdfBlob);
            console.log('Created PDF blob URL:', url);
            return url;
        } else if (contentType.includes('msword') || contentType.includes('officedocument') || fileType === 'doc') {
            return URL.createObjectURL(blob);
        } else {
            return URL.createObjectURL(blob);
        }
    }

    function handleFileClick(uri) {
        setFileModalUri(uri);
        setFileModalFilename(getFilenameFromUri(uri));
        setFileModalOpen(true);
    }

    return (
        <div className="container-fluid py-4">
            <div className="row mb-3">
                <div className="col-12 d-flex justify-content-between align-items-center">
                    <h1 className="h3 mb-0">
                        <i className="bi bi-search me-2" />
                        Vector Inspector
                    </h1>
                </div>
            </div>

            {/* Note: client and doc-type selectors moved into the Knowledge-base Files panel filters below */}

            {/* Full width preview row */}


            {/* Review Modal */}
            {/* Details Modal (Vectors Summary + Preview for a KB file) */}
            {detailsModalOpen && (
                <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog">
                    <div className="modal-dialog modal-xl" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">File Details - {detailsFileKey ? (
                                    <button type="button" className="btn btn-link p-0" style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => handleFileClick(detailsFileKey)}>
                                        {getFilenameFromUri(detailsFileKey)}
                                    </button>
                                ) : (<span>N/A</span>)}</h5>
                                <button type="button" className="btn-close" onClick={closeDetailsModal}></button>
                            </div>
                            <div className="modal-body">
                                {detailsLoading ? (
                                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '160px' }}>
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <h5>Vectors Summary</h5>
                                        <div className="mb-2">
                                            <div className="d-flex justify-content-between text-muted small">
                                                <div><strong>Date:</strong> {stats.epochDisplay}</div>
                                                <div className="text-center"><strong>Total vectors:</strong> {(detailsVectors || []).length}</div>
                                                <div className="text-end"><strong>Total text length:</strong> {(detailsVectors || []).reduce((acc, v) => acc + ((v && v.text) ? v.text.length : 0), 0)} chars</div>
                                            </div>
                                        </div>

                                        {/* Doc Type, Sub Type, and Account Number row */}
                                        <div className="row mb-3">
                                            <div className="col-md-4">
                                                <label className="form-label small mb-1"><strong>Doc Type:</strong></label>
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={(fileAttributes && fileAttributes.doc_type_id) || ''}
                                                    onChange={(e) => {
                                                        const selectedId = e.target.value;
                                                        const selectedDocType = docTypesData.find(dt => dt.id === selectedId);
                                                        const selectedName = selectedDocType?.name || '';
                                                        const currentSubTypeId = fileAttributes?.sub_type_id || '';
                                                        const filteredSubs = getFilteredSubTypes(selectedId);
                                                        // Clear sub_type if it's not valid for the new doc_type
                                                        const validSub = filteredSubs.find(st => st.id === currentSubTypeId);
                                                        const newSubTypeId = validSub ? currentSubTypeId : '';
                                                        const newSubTypeName = validSub ? validSub.name : '';
                                                        setFileAttributes(prev => ({ ...(prev || {}), doc_type: selectedName, doc_type_id: selectedId, sub_type: newSubTypeName, sub_type_id: newSubTypeId }));
                                                        try { markDirty({ ...(fileAttributes || {}), doc_type_id: selectedId, sub_type_id: newSubTypeId }); } catch (err) { console.warn('Failed to mark doc_type dirty', err); }
                                                    }}
                                                >
                                                    <option value="">Select doc type...</option>
                                                    {docTypesData.map((dt) => (<option key={dt.id} value={dt.id}>{dt.name}</option>))}
                                                </select>
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label small mb-1"><strong>Sub Type:</strong></label>
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={(fileAttributes && fileAttributes.sub_type_id) || ''}
                                                    onChange={(e) => {
                                                        const selectedId = e.target.value;
                                                        const filteredSubs = getFilteredSubTypes(fileAttributes?.doc_type_id);
                                                        const selectedSub = filteredSubs.find(st => st.id === selectedId);
                                                        const selectedName = selectedSub?.name || '';
                                                        setFileAttributes(prev => ({ ...(prev || {}), sub_type: selectedName, sub_type_id: selectedId }));
                                                        try { markDirty({ ...(fileAttributes || {}), sub_type_id: selectedId }); } catch (err) { console.warn('Failed to mark sub_type dirty', err); }
                                                    }}
                                                >
                                                    <option value="">Select sub type...</option>
                                                    {getFilteredSubTypes(fileAttributes?.doc_type_id).map((st) => (<option key={st.id} value={st.id}>{st.name}</option>))}
                                                </select>
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label small mb-1"><strong>Account Number:</strong></label>
                                                <input
                                                    type="text"
                                                    className={`form-control form-control-sm ${fileAttributes?.account_number && !/^(x\d{4}|Mixed|NONE|)$/i.test(fileAttributes.account_number) ? 'is-invalid' : ''}`}
                                                    placeholder="e.g., x1234, Mixed, or NONE"
                                                    value={(fileAttributes && fileAttributes.account_number) || ''}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        setFileAttributes(prev => ({ ...(prev || {}), account_number: val }));
                                                        try { markDirty({ ...(fileAttributes || {}), account_number: val }); } catch (err) { console.warn('Failed to mark account_number dirty', err); }
                                                    }}
                                                />
                                                {fileAttributes?.account_number && !/^(x\d{4}|Mixed|NONE|)$/i.test(fileAttributes.account_number) && (
                                                    <div className="invalid-feedback">
                                                        Format must be x followed by 4 digits (e.g., x1234), "Mixed", or "NONE"
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="mb-3 d-flex align-items-start">
                                            <div style={{ flex: 1 }}>
                                                <strong>Vectors Purpose:</strong>
                                                <div className="d-flex align-items-center">
                                                    <div className="text-muted small me-3" style={{ wordBreak: 'break-word' }}>{vectorPurpose || 'N/A'}</div>
                                                    <div className="d-flex align-items-center">
                                                        <div className="form-check form-check-inline me-2">
                                                            <input className="form-check-input" type="checkbox" id="vectorAcceptedInlineModal" checked={!!(fileAttributes && fileAttributes.vector_purpose_accepted)} onChange={e => handleToggleAccepted('vector', e.target.checked)} />
                                                            <label className="form-check-label small text-muted" htmlFor="vectorAcceptedInlineModal">Accepted</label>
                                                        </div>
                                                        <button type="button" className="btn btn-sm p-0" title="Mark as best purpose" onClick={() => handleSetBestPurpose('vector')} style={{ color: (fileAttributes && fileAttributes.best_purpose) === 'vector' ? '#b8860b' : '#888', textDecoration: 'none', border: 'none', background: 'transparent' }}>
                                                            <span style={{ fontSize: '18px', lineHeight: 1 }}>{(fileAttributes && fileAttributes.best_purpose) === 'vector' ? '★' : '☆'}</span>
                                                        </button>
                                                        <div className="ms-2">
                                                            <button className="btn btn-sm btn-outline-primary" onClick={handleAnalyzeVectorsPurpose} disabled={analyzingVectorPurpose || !(canAnalyzeVectorsPurpose)}>{analyzingVectorPurpose ? 'Analyzing...' : 'Analyze'}</button>
                                                        </div>
                                                    </div>
                                                </div>
                                                {/* Notes Text Box for Vector Purpose */}
                                                <div className="mt-2">
                                                    <label className="form-label small">Notes</label>
                                                    <textarea
                                                        className="form-control form-control-sm"
                                                        rows={2}
                                                        value={fileAttributes?.vector_purpose_notes || ''}
                                                        onChange={e => {
                                                            const val = e.target.value;
                                                            setFileAttributes(prev => ({ ...(prev || {}), vector_purpose_notes: val }));
                                                            try { markDirty({ ...(fileAttributes || {}), vector_purpose_notes: val }); } catch (err) { console.warn('Failed to mark vector notes dirty', err); }
                                                        }}
                                                        placeholder="Add notes about the vector purpose..."
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mb-3 d-flex align-items-start">
                                            <div style={{ flex: 1 }}>
                                                <strong>Document Purpose:</strong>
                                                <div className="d-flex align-items-center">
                                                    <div className="text-muted small me-3" style={{ wordBreak: 'break-word' }}>{documentPurpose || 'N/A'}</div>
                                                    <div className="d-flex align-items-center">
                                                        <div className="form-check form-check-inline me-2">
                                                            <input className="form-check-input" type="checkbox" id="fileAcceptedInlineModal" checked={!!(fileAttributes && fileAttributes.file_purpose_accepted)} onChange={e => handleToggleAccepted('file', e.target.checked)} />
                                                            <label className="form-check-label small text-muted" htmlFor="fileAcceptedInlineModal">Accepted</label>
                                                        </div>
                                                        <button type="button" className="btn btn-sm p-0" title="Mark as best purpose" onClick={() => handleSetBestPurpose('file')} style={{ color: (fileAttributes && fileAttributes.best_purpose) === 'file' ? '#b8860b' : '#888', textDecoration: 'none', border: 'none', background: 'transparent' }}>
                                                            <span style={{ fontSize: '18px', lineHeight: 1 }}>{(fileAttributes && fileAttributes.best_purpose) === 'file' ? '★' : '☆'}</span>
                                                        </button>
                                                        <div className="ms-2">
                                                            <button className="btn btn-sm btn-outline-primary" onClick={handleAnalyzeDocumentPurpose} disabled={analyzingDocumentPurpose || !selectedFile}>{analyzingDocumentPurpose ? 'Analyzing...' : 'Analyze'}</button>
                                                        </div>
                                                    </div>
                                                </div>
                                                {/* Notes Text Box for Document Purpose */}
                                                <div className="mt-2">
                                                    <label className="form-label small">Notes</label>
                                                    <textarea
                                                        className="form-control form-control-sm"
                                                        rows={2}
                                                        value={fileAttributes?.file_purpose_notes || ''}
                                                        onChange={e => {
                                                            const val = e.target.value;
                                                            setFileAttributes(prev => ({ ...(prev || {}), file_purpose_notes: val }));
                                                            try { markDirty({ ...(fileAttributes || {}), file_purpose_notes: val }); } catch (err) { console.warn('Failed to mark file notes dirty', err); }
                                                        }}
                                                        placeholder="Add notes about the document purpose..."
                                                    />
                                                </div>
                                            </div>

                                        </div>

                                        <hr />
                                        <h5>Vector Details</h5>
                                        {Array.isArray(detailsVectors) && detailsVectors.length > 0 ? (
                                            <div className="table-responsive mt-2" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                                <table className="table table-sm table-striped">
                                                    <thead>
                                                        <tr>
                                                            <th>Text</th>
                                                            <th>Analysis</th>
                                                            <th>Manual Review</th>
                                                            <th>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {detailsVectors.map((v, idx) => {
                                                            const analysis = analysisResults[v.key] || v.analysis;
                                                            const manual = manualReviews[v.key] || v.manual_review || null;
                                                            const isAnalyzing = analyzingVectors.has(v.key);
                                                            return (
                                                                <tr key={v.key || idx} title={v.key}>
                                                                    <td style={{ maxWidth: '600px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'normal', wordBreak: 'break-word', cursor: 'pointer', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }} onClick={() => showTextDetails(v.text || '', v.key)}>
                                                                        {v.text || ''}
                                                                    </td>
                                                                    <td style={{ minWidth: '240px', maxWidth: '420px' }}>
                                                                        {analysis ? (
                                                                            <div>
                                                                                <span className={`${getCategoryBadgeClass(analysis.category)} cursor-pointer`} onClick={() => showAnalysisDetails(v.key, analysis)} style={{ cursor: 'pointer' }}>{analysis.category?.toUpperCase() || 'N/A'}</span>
                                                                                {analysis.score && (<small className="text-muted ms-2">({analysis.score}/100)</small>)}
                                                                                {analysis.what && (<div className="small text-muted mt-1" style={{ maxWidth: '360px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={analysis.what}>{analysis.what}</div>)}
                                                                            </div>
                                                                        ) : isAnalyzing ? (
                                                                            <div className="spinner-border spinner-border-sm text-primary" role="status"><span className="visually-hidden">Analyzing...</span></div>
                                                                        ) : (<span className="text-muted">Not analyzed</span>)}
                                                                    </td>
                                                                    <td style={{ minWidth: '240px', maxWidth: '420px' }}>
                                                                        {manual ? (
                                                                            <div>
                                                                                <span className={`${getCategoryBadgeClass(manual.category)} cursor-pointer`} onClick={() => showAnalysisDetails(v.key, manual)} style={{ cursor: 'pointer' }}>{manual.category?.toUpperCase() || 'N/A'}</span>
                                                                                {manual.score && (<small className="text-muted ms-2">({manual.score}/100)</small>)}
                                                                                {manual.what && (<div className="small text-muted mt-1" style={{ maxWidth: '360px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={manual.what}>{manual.what}</div>)}
                                                                            </div>
                                                                        ) : (<span className="text-muted">No manual review</span>)}
                                                                    </td>
                                                                    <td>
                                                                        <button className="btn btn-sm btn-outline-primary me-2" disabled={isAnalyzing} onClick={() => handleAnalyzeVector(v)}>{isAnalyzing ? 'Analyzing...' : 'Analyze'}</button>
                                                                        <button className="btn btn-sm btn-outline-secondary" onClick={() => openReviewModal(v)}>Review</button>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <div className="text-muted">No vectors to display.</div>
                                        )}
                                        
                                        {/* Vectors pagination controls */}
                                        <PaginationControls
                                            currentCount={detailsVectors.length}
                                            totalCount={vectorsPagination.totalCount}
                                            hasMore={vectorsPagination.hasMore}
                                            onLoadMore={handleLoadMoreVectors}
                                            loading={loadingMoreVectors}
                                            itemType="vectors"
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeDetailsModal}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Summary Modal (Document Preview */}
            {summaryModalOpen && (
                <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog">
                    <div className="modal-dialog" style={{ maxWidth: '80%', width: '80%' }} role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">File Summary - {summaryFileKey ? getFilenameFromUri(summaryFileKey) : 'N/A'}</h5>
                                <button type="button" className="btn-close" onClick={closeSummaryModal}></button>
                            </div>
                            <div className="modal-body d-flex flex-column">
                                {summaryLoading ? (
                                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="row" style={{ height: 'calc(100vh - 225px)' }}>
                                        {/* Left side - Document Preview */}
                                        <div className="col-md-6 d-flex flex-column" style={{ height: '100%' }}>
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <h6 className="mb-0">Document Preview</h6>
                                                {!summaryContentLoading && !summaryError && summaryContent && (() => {
                                                    const filename = getFilenameFromUri(summaryFileKey);
                                                    const fileType = getFileType(filename);
                                                    return fileType === 'text' ? (
                                                        <div className="form-check form-switch">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                id="documentWordWrapToggle"
                                                                checked={wordWrap}
                                                                onChange={(e) => setWordWrap(e.target.checked)}
                                                            />
                                                            <label className="form-check-label small" htmlFor="documentWordWrapToggle">
                                                                Word wrap
                                                            </label>
                                                        </div>
                                                    ) : null;
                                                })()}
                                            </div>
                                            <div className="border rounded p-3" style={{ flex: '1', overflow: 'auto', minHeight: '0' }}>
                                                {summaryContentLoading ? (
                                                    <div className="d-flex justify-content-center align-items-center h-100">
                                                        <div className="spinner-border text-primary" role="status">
                                                            <span className="visually-hidden">Loading content...</span>
                                                        </div>
                                                    </div>
                                                ) : summaryError ? (
                                                    <div className="text-danger text-center d-flex align-items-center justify-content-center h-100">
                                                        <div>
                                                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                                            {summaryError}
                                                        </div>
                                                    </div>
                                                ) : summaryContent ? (
                                                    (() => {
                                                        const filename = getFilenameFromUri(summaryFileKey);
                                                        const fileType = getFileType(filename);
                                                        
                                                        // Determine actual content type based on content, not just filename
                                                        const isBlobUrl = typeof summaryContent === 'string' && summaryContent.startsWith('blob:');
                                                        const isTextContent = typeof summaryContent === 'string' && !isBlobUrl;

                                                        // If content is text (not a blob URL), display as text
                                                        if (isTextContent) {
                                                            const isHtml = /<html|<!DOCTYPE html/i.test(summaryContent);
                                                            if (isHtml) {
                                                                return <div className="text-danger">Unable to display file content (HTML received instead of text file).</div>;
                                                            }
                                                            return (
                                                                <pre
                                                                    style={{
                                                                        overflow: 'auto',
                                                                        fontSize: '12px',
                                                                        whiteSpace: wordWrap ? 'pre-wrap' : 'pre',
                                                                        wordBreak: wordWrap ? 'break-word' : 'normal'
                                                                    }}
                                                                >
                                                                    {summaryContent}
                                                                </pre>
                                                            );
                                                        }
                                                        
                                                        // For blob URLs, use filename-based detection
                                                        if (fileType === 'image') {
                                                            return <img src={summaryContent} alt={filename} style={{ maxWidth: '100%', height: 'auto' }} />;
                                                        } else if (fileType === 'pdf') {
                                                            return <iframe src={summaryContent} title={filename} style={{ width: '100%', height: '99%', border: 'none', overflow: 'hidden' }} />;
                                                        } else if (fileType === 'doc') {
                                                            return <iframe src={summaryContent} title={filename} style={{ width: '100%', height: '99%', border: 'none', overflow: 'hidden' }} />;
                                                        } else {
                                                            return (
                                                                <div className="text-center" style={{ padding: '100px 0' }}>
                                                                    <i className="bi bi-file-earmark me-2"></i>
                                                                    <div>Preview not available for this file type</div>
                                                                    <a href={summaryContent} download={filename} className="btn btn-primary mt-3">Download File</a>
                                                                </div>
                                                            );
                                                        }
                                                    })()
                                                ) : (
                                                    <div className="text-muted text-center d-flex align-items-center justify-content-center h-100">
                                                        No content available
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Right side - Doc Type, Sub Type, Account Number */}
                                        <div className="col-md-6 d-flex flex-column" style={{ height: '100%' }}>
                                            <h6>File Attributes</h6>

                                            <div className="row mb-3">
                                                <div className="col-md-4">
                                                    <label className="form-label small mb-1"><strong>Doc Type:</strong></label>
                                                    <select
                                                        className="form-select form-select-sm"
                                                        value={(summaryFileAttributes && summaryFileAttributes.doc_type_id) || ''}
                                                        onChange={(e) => {
                                                            const selectedId = e.target.value;
                                                            const selectedDocType = docTypesData.find(dt => dt.id === selectedId);
                                                            const selectedName = selectedDocType?.name || '';
                                                            const currentSubTypeId = summaryFileAttributes?.sub_type_id || '';
                                                            const filteredSubs = getFilteredSubTypes(selectedId);
                                                            // Clear sub_type if it's not valid for the new doc_type
                                                            const validSub = filteredSubs.find(st => st.id === currentSubTypeId);
                                                            const newSubTypeId = validSub ? currentSubTypeId : '';
                                                            const newSubTypeName = validSub?.name || '';
                                                            setSummaryFileAttributes(prev => ({ ...(prev || {}), doc_type: selectedName, doc_type_id: selectedId, sub_type: newSubTypeName, sub_type_id: newSubTypeId }));
                                                            try {
                                                                vectorService.updateFile(summaryFileKey, { ...(summaryFileAttributes || {}), doc_type_id: selectedId, sub_type_id: newSubTypeId });
                                                                setKbFilesSummary(prevSummary => {
                                                                    if (!Array.isArray(prevSummary)) return prevSummary;
                                                                    return prevSummary.map(f => {
                                                                        if (f.s3_key === summaryFileKey) {
                                                                            return { ...f, doc_type: selectedName || 'Mixed', doc_type_id: selectedId, sub_type: newSubTypeName || 'Mixed', sub_type_id: newSubTypeId };
                                                                        }
                                                                        return f;
                                                                    });
                                                                });
                                                            } catch (err) {
                                                                console.warn('Failed to update doc_type', err);
                                                            }
                                                        }}
                                                    >
                                                        <option value="">Select doc type...</option>
                                                        {docTypesData.map((dt) => (<option key={dt.id} value={dt.id}>{dt.name}</option>))}
                                                    </select>
                                                </div>

                                                <div className="col-md-4">
                                                    <label className="form-label small mb-1"><strong>Sub Type:</strong></label>
                                                    <select
                                                        className="form-select form-select-sm"
                                                        value={(summaryFileAttributes && summaryFileAttributes.sub_type_id) || ''}
                                                        onChange={(e) => {
                                                            const selectedId = e.target.value;
                                                            const filteredSubs = getFilteredSubTypes(summaryFileAttributes?.doc_type_id);
                                                            const selectedSub = filteredSubs.find(st => st.id === selectedId);
                                                            const selectedName = selectedSub?.name || '';
                                                            setSummaryFileAttributes(prev => ({ ...(prev || {}), sub_type: selectedName, sub_type_id: selectedId }));
                                                            try {
                                                                vectorService.updateFile(summaryFileKey, { ...(summaryFileAttributes || {}), sub_type_id: selectedId });
                                                                setKbFilesSummary(prevSummary => {
                                                                    if (!Array.isArray(prevSummary)) return prevSummary;
                                                                    return prevSummary.map(f => {
                                                                        if (f.s3_key === summaryFileKey) {
                                                                            return { ...f, sub_type: selectedName || 'Mixed', sub_type_id: selectedId };
                                                                        }
                                                                        return f;
                                                                    });
                                                                });
                                                            } catch (err) {
                                                                console.warn('Failed to update sub_type', err);
                                                            }
                                                        }}
                                                    >
                                                        <option value="">Select sub type...</option>
                                                        {getFilteredSubTypes(summaryFileAttributes?.doc_type_id).map((st) => (<option key={st.id} value={st.id}>{st.name}</option>))}
                                                    </select>
                                                </div>

                                                <div className="col-md-4">
                                                    <label className="form-label small mb-1"><strong>Account Number:</strong></label>
                                                    <input
                                                        type="text"
                                                        className={`form-control form-control-sm ${summaryFileAttributes?.account_number && !/^(x\d{4}|Mixed|NONE|)$/i.test(summaryFileAttributes.account_number) ? 'is-invalid' : ''}`}
                                                        placeholder="e.g., x1234, Mixed, or NONE"
                                                        value={(summaryFileAttributes && summaryFileAttributes.account_number) || ''}
                                                        onChange={(e) => {
                                                            const val = e.target.value;
                                                            setSummaryFileAttributes(prev => ({ ...(prev || {}), account_number: val }));
                                                            try {
                                                                vectorService.updateFile(summaryFileKey, { ...(summaryFileAttributes || {}), account_number: val });
                                                                setKbFilesSummary(prevSummary => {
                                                                    if (!Array.isArray(prevSummary)) return prevSummary;
                                                                    return prevSummary.map(f => {
                                                                        if (f.s3_key === summaryFileKey) {
                                                                            return { ...f, account_number: val };
                                                                        }
                                                                        return f;
                                                                    });
                                                                });
                                                            } catch (err) {
                                                                console.warn('Failed to update account_number', err);
                                                            }
                                                        }}
                                                    />
                                                    {summaryFileAttributes?.account_number && !/^(x\d{4}|Mixed|NONE|)$/i.test(summaryFileAttributes.account_number) && (
                                                        <div className="invalid-feedback">
                                                            Format must be x followed by 4 digits (e.g., x1234), "Mixed", or "NONE"
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="d-flex flex-column" style={{ flex: '1', minHeight: '0' }}>
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <div className="d-flex align-items-center">
                                                        <label className="form-label small mb-0 me-2"><strong>Document Summary:</strong></label>
                                                        {isCustomSummary && (
                                                            <span className="badge bg-warning text-dark small">
                                                                <i className="bi bi-magic me-1"></i>Custom
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="d-flex gap-2">
                                                        <button
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={handleRegenerateDocumentSummary}
                                                            disabled={documentSummaryLoading}
                                                        >
                                                            {documentSummaryLoading ? (
                                                                <>
                                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                                    Generating...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <i className="bi bi-arrow-clockwise me-2"></i>
                                                                    Default Summary
                                                                </>
                                                            )}
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-outline-secondary"
                                                            onClick={handleOpenCustomSummaryModal}
                                                            disabled={documentSummaryLoading}
                                                        >
                                                            <i className="bi bi-pencil-square me-2"></i>
                                                            Custom Summary
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-outline-success"
                                                            onClick={(e) => {
                                                                if (editedDocumentSummary || documentSummary) {
                                                                    const textToCopy = editedDocumentSummary || documentSummary;
                                                                    // Convert HTML to clean markdown/text, stripping annotations
                                                                    const cleanText = htmlToMarkdown(textToCopy);
                                                                    navigator.clipboard.writeText(cleanText).then(() => {
                                                                        // Show temporary success feedback
                                                                        const button = e.target.closest('button');
                                                                        const originalIcon = button.querySelector('i').className;
                                                                        button.querySelector('i').className = 'bi bi-check-lg';
                                                                        button.classList.remove('btn-outline-success');
                                                                        button.classList.add('btn-success');
                                                                        setTimeout(() => {
                                                                            button.querySelector('i').className = originalIcon;
                                                                            button.classList.remove('btn-success');
                                                                            button.classList.add('btn-outline-success');
                                                                        }, 1500);
                                                                    }).catch(err => {
                                                                        console.error('Failed to copy to clipboard:', err);
                                                                        alert('Failed to copy to clipboard');
                                                                    });
                                                                }
                                                            }}
                                                            disabled={documentSummaryLoading || (!editedDocumentSummary && !documentSummary)}
                                                            title="Copy summary to clipboard"
                                                        >
                                                            <i className="bi bi-clipboard"></i>
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-outline-primary"
                                                            onClick={handleExportSummaryToPdf}
                                                            disabled={!documentSummary && !editedDocumentSummary}
                                                            title="Export summary to PDF"
                                                        >
                                                            <i className="bi bi-download"></i>
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-outline-info"
                                                            onClick={() => {
                                                                setTriggerSummaryEdit(true);
                                                                setTimeout(() => setTriggerSummaryEdit(false), 100);
                                                            }}
                                                            disabled={documentSummaryLoading}
                                                            title="Edit summary"
                                                        >
                                                            <i className="bi bi-pencil"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                                <div style={{ flex: '1', minHeight: '0', overflow: 'auto' }}>
                                                    {documentSummaryLoading ? (
                                                        <div className="text-center text-muted border rounded p-3" style={{ padding: '40px 0', backgroundColor: '#f8f9fa' }}>
                                                            <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                                                            Generating summary...
                                                        </div>
                                                    ) : (
                                                        <SummaryEditor
                                                            value={editedDocumentSummary || documentSummary}
                                                            originalSummary={originalDocumentSummary}
                                                            onChange={(newValue) => {
                                                                setEditedDocumentSummary(newValue);
                                                                setDocumentSummary(newValue);
                                                            }}
                                                            disabled={documentSummaryLoading}
                                                            externalEditTrigger={triggerSummaryEdit}
                                                            onEditModeChange={(viewState) => {
                                                                setSummaryViewState(viewState);
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeSummaryModal}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {reviewModalOpen && (
                <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog">
                    <div className="modal-dialog modal-lg" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Manual Review - {reviewVectorKey}</h5>
                                <button type="button" className="btn-close" onClick={() => setReviewModalOpen(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Category</label>
                                    <select className="form-select" value={reviewForm.category} onChange={e => setReviewForm({ ...reviewForm, category: e.target.value })}>
                                        <option value="">Choose...</option>
                                        <option value="signal">Signal</option>
                                        <option value="noise">Noise</option>
                                        <option value="maybe">Maybe</option>
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Score (1-100)</label>
                                    <input type="number" className="form-control" value={reviewForm.score} onChange={e => setReviewForm({ ...reviewForm, score: e.target.value })} min={1} max={100} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">What</label>
                                    <input className="form-control" value={reviewForm.what} onChange={e => setReviewForm({ ...reviewForm, what: e.target.value })} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Why</label>
                                    <textarea className="form-control" rows={3} value={reviewForm.why} onChange={e => setReviewForm({ ...reviewForm, why: e.target.value })}></textarea>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">AI Reasoning (thinking)</label>
                                    <textarea className="form-control" rows={4} value={reviewForm.thinking} onChange={e => setReviewForm({ ...reviewForm, thinking: e.target.value })}></textarea>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setReviewModalOpen(false)}>Cancel</button>
                                <button type="button" className="btn btn-primary" onClick={submitReview}>Save Review</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {reviewModalOpen && <div className="modal-backdrop fade show"></div>}

            {/* Analysis Details Modal */}
            {showAnalysisModal && selectedAnalysis && (
                <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog">
                    <div className="modal-dialog modal-lg" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Analysis Details</h5>
                                <button type="button" className="btn-close" onClick={() => setShowAnalysisModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <strong>Vector ID:</strong>
                                    <div className="text-muted small">{selectedAnalysis.vectorKey}</div>
                                </div>

                                <div className="mb-3">
                                    <strong>Classification:</strong>
                                    <div>
                                        <span className={getCategoryBadgeClass(selectedAnalysis.category)}>
                                            {selectedAnalysis.category?.toUpperCase() || 'N/A'}
                                        </span>
                                        {selectedAnalysis.score && (
                                            <span className="ms-2 text-muted">Score: {selectedAnalysis.score}/100</span>
                                        )}
                                    </div>
                                </div>

                                {selectedAnalysis.what && (
                                    <div className="mb-3">
                                        <strong>What:</strong>
                                        <div className="text-muted">{selectedAnalysis.what}</div>
                                    </div>
                                )}

                                {selectedAnalysis.why && (
                                    <div className="mb-3">
                                        <strong>Why:</strong>
                                        <div className="text-muted">{selectedAnalysis.why}</div>
                                    </div>
                                )}

                                {selectedAnalysis.thinking && (
                                    <div className="mb-3">
                                        <strong>AI Reasoning:</strong>
                                        <div className="text-muted small bg-light p-2 rounded">{selectedAnalysis.thinking}</div>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowAnalysisModal(false)}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showAnalysisModal && <div className="modal-backdrop fade show"></div>}
            {/* Full Text Modal */}
            {showTextModal && (
                <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog">
                    <div className="modal-dialog modal-lg" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Full Text {selectedTextKey ? `- ${selectedTextKey}` : ''}</h5>
                                <button type="button" className="btn-close" onClick={() => setShowTextModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div style={{ whiteSpace: 'pre-wrap' }} className="text-muted">{selectedTextFull}</div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowTextModal(false)}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showTextModal && <div className="modal-backdrop fade show"></div>}
            {/* Doc Types Management Modal */}
            {docTypesModalOpen && (
                <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog">
                    <div className="modal-dialog" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Manage Doc Types</h5>
                                <button type="button" className="btn-close" onClick={() => setDocTypesModalOpen(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Add Doc Type</label>
                                    <div className="input-group">
                                        <input className="form-control" value={docTypeModalInput} onChange={e => setDocTypeModalInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddDocTypeFromModal(); } }} />
                                        <button className="btn btn-primary" type="button" onClick={handleAddDocTypeFromModal} disabled={docTypeModalBusy}>{docTypeModalBusy ? 'Working...' : 'Add'}</button>
                                    </div>
                                </div>
                                <div>
                                    <label className="form-label">Existing Doc Types</label>
                                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                        {(docTypesList || []).length === 0 && <div className="text-muted">No doc types</div>}
                                        {(docTypesList || []).map((dt) => (
                                            <div key={dt} className="d-flex justify-content-between align-items-center mb-2">
                                                <div style={{ flex: 1 }}>
                                                    {renamingDocType === dt ? (
                                                        <div className="d-flex">
                                                            <input className="form-control form-control-sm me-2" value={renameDocTypeInput} onChange={e => setRenameDocTypeInput(e.target.value)} />
                                                            <button className="btn btn-sm btn-primary me-2" onClick={handleConfirmRenameDocType} disabled={docTypeModalBusy}>Save</button>
                                                            <button className="btn btn-sm btn-secondary" onClick={handleCancelRenameDocType} disabled={docTypeModalBusy}>Cancel</button>
                                                        </div>
                                                    ) : (
                                                        <div>{dt}</div>
                                                    )}
                                                </div>
                                                <div>
                                                    {renamingDocType !== dt && (
                                                        <>
                                                            <button className="btn btn-sm btn-outline-secondary me-2" onClick={() => handleStartRenameDocType(dt)} disabled={docTypeModalBusy}>Rename</button>
                                                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteDocType(dt)} disabled={docTypeModalBusy}>Delete</button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setDocTypesModalOpen(false)}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {docTypesModalOpen && <div className="modal-backdrop fade show"></div>}
            {/* Sub Types Management Modal */}
            {subTypesModalOpen && (
                <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog">
                    <div className="modal-dialog" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Manage Sub Types</h5>
                                <button type="button" className="btn-close" onClick={() => setSubTypesModalOpen(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Add Sub Type</label>
                                    <div className="input-group">
                                        <input className="form-control" value={subTypeModalInput} onChange={e => setSubTypeModalInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddSubTypeFromModal(); } }} />
                                        <button className="btn btn-primary" type="button" onClick={handleAddSubTypeFromModal} disabled={subTypeModalBusy}>{subTypeModalBusy ? 'Working...' : 'Add'}</button>
                                    </div>
                                </div>
                                <div>
                                    <label className="form-label">Existing Sub Types</label>
                                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                        {(subTypesList || []).length === 0 && <div className="text-muted">No sub types</div>}
                                        {(subTypesList || []).map((st) => (
                                            <div key={st} className="d-flex justify-content-between align-items-center mb-2">
                                                <div style={{ flex: 1 }}>
                                                    {renamingSubType === st ? (
                                                        <div className="d-flex">
                                                            <input className="form-control form-control-sm me-2" value={renameSubTypeInput} onChange={e => setRenameSubTypeInput(e.target.value)} />
                                                            <button className="btn btn-sm btn-primary me-2" onClick={handleConfirmRenameSubType} disabled={subTypeModalBusy}>Save</button>
                                                            <button className="btn btn-sm btn-secondary" onClick={handleCancelRenameSubType} disabled={subTypeModalBusy}>Cancel</button>
                                                        </div>
                                                    ) : (
                                                        <div>{st}</div>
                                                    )}
                                                </div>
                                                <div>
                                                    {renamingSubType !== st && (
                                                        <>
                                                            <button className="btn btn-sm btn-outline-secondary me-2" onClick={() => handleStartRenameSubType(st)} disabled={subTypeModalBusy}>Rename</button>
                                                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteSubType(st)} disabled={subTypeModalBusy}>Delete</button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setSubTypesModalOpen(false)}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {subTypesModalOpen && <div className="modal-backdrop fade show"></div>}
            {/* Knowledge-base files summary table */}
            <div className="row mt-4">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <h5 className="card-title mb-0">Knowledge-base Files</h5>
                                    <p className="text-muted mb-0">All KB files for the selected client/doc type.</p>
                                </div>
                                <div className="text-end">
                                    <div className="small text-muted">Total files</div>
                                    <div><span className="badge bg-secondary">{(kbFilesSummary && kbFilesSummary.length) || 0}</span></div>
                                </div>
                            </div>

                            <div>
                                <div className="d-flex mb-2 align-items-center">
                                    <div style={{ minWidth: '380px' }} className="me-3 d-flex">
                                        <div style={{ flex: 1, marginRight: '8px' }}>
                                            <label htmlFor="clientSelect" className="form-label small mb-1">Select Client</label>
                                            <select id="clientSelect" className="form-select form-select-sm" value={selectedClient} onChange={e => setSelectedClient(e.target.value)}>
                                                <option value="">Choose a client...</option>
                                                {clients.map(c => (
                                                    <option key={c.client_id} value={c.client_id}>{c.client_name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div style={{ width: '180px' }}>
                                            <label htmlFor="docTypeSelect" className="form-label small mb-1">Doc Type</label>
                                            <select id="docTypeSelect" className="form-select form-select-sm" value={docType} onChange={e => {
                                                const v = e.target.value;
                                                // show loading spinner immediately for better UX
                                                setDocType(v);
                                                setLoadingKbFilesSummary(true);
                                                // clear previous files selection while fetching
                                                setKbFiles([]);
                                                setSelectedFile('');
                                            }} disabled={!selectedClient}>
                                                <option value="">Choose a doc type...</option>
                                                <option value="emails">Emails</option>
                                                <option value="files">Files</option>
                                                <option value="notes">Notes</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div style={{ minWidth: '240px' }} className="me-2">
                                        <label className="form-label small mb-1">Filter by Filename</label>
                                        <input className="form-control form-control-sm" placeholder="e.g. *.pdf, report-202?, part*" value={filterFilename} onChange={e => setFilterFilename(e.target.value)} />
                                    </div>
                                    <div style={{ minWidth: '150px' }} className="me-2">
                                        <label className="form-label small mb-1">Filter by Doc Type</label>
                                        <select className="form-select form-select-sm" value={filterDocType} onChange={e => setFilterDocType(e.target.value)}>
                                            <option value="">All doc types</option>
                                            <option value="N/A">N/A (none)</option>
                                            {docTypesList.map(opt => (<option key={opt} value={opt}>{opt}</option>))}
                                        </select>
                                    </div>
                                    <div style={{ minWidth: '180px' }} className="me-2">
                                        <label className="form-label small mb-1">Filter by Sub Type</label>
                                        <select className="form-select form-select-sm" value={filterSubType} onChange={e => setFilterSubType(e.target.value)}>
                                            <option value="">All sub types</option>
                                            <option value="N/A">N/A (none)</option>
                                            {subTypesList.map(opt => (<option key={opt} value={opt}>{opt}</option>))}
                                        </select>
                                    </div>
                                    <div style={{ minWidth: '150px' }} className="me-2">
                                        <label className="form-label small mb-1">Filter by Account</label>
                                        <input className="form-control form-control-sm" placeholder="e.g. x1234" value={filterAccountNumber} onChange={e => setFilterAccountNumber(e.target.value)} />
                                    </div>
                                    <div className="ms-auto d-flex align-items-end">
                                        <button className="btn btn-sm btn-outline-secondary me-2" type="button" onClick={() => { setFilterDocType(''); setFilterSubType(''); setFilterAccountNumber(''); setFilterFilename(''); }}>Clear filters</button>
                                    </div>
                                </div>
                                {loadingKbFilesSummary ? (
                                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100px' }}>
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-sm table-striped">
                                            <thead>
                                                <tr>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => toggleKbSort('name')}>Filename {kbSort.by === 'name' ? (kbSort.dir === 'asc' ? '▲' : '▼') : ''}</th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => toggleKbSort('doc_type')}>Doc Type {kbSort.by === 'doc_type' ? (kbSort.dir === 'asc' ? '▲' : '▼') : ''}</th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => toggleKbSort('sub_type')}>Sub Type {kbSort.by === 'sub_type' ? (kbSort.dir === 'asc' ? '▲' : '▼') : ''}</th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => toggleKbSort('account_number')}>Account {kbSort.by === 'account_number' ? (kbSort.dir === 'asc' ? '▲' : '▼') : ''}</th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => toggleKbSort('dateEpochMs')}>Date {kbSort.by === 'dateEpochMs' ? (kbSort.dir === 'asc' ? '▲' : '▼') : ''}</th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => toggleKbSort('totalVectors')}>Total Vectors {kbSort.by === 'totalVectors' ? (kbSort.dir === 'asc' ? '▲' : '▼') : ''}</th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => toggleKbSort('totalTextLength')}>Total Text Length {kbSort.by === 'totalTextLength' ? (kbSort.dir === 'asc' ? '▲' : '▼') : ''}</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(sortedKbFiles || []).map((f) => (
                                                    <tr key={f.s3_key || f.name}>
                                                        <td>
                                                            {f.name ? (
                                                                <button type="button" className="btn btn-link p-0" style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => handleFileClick(f.s3_key || f.name)}>
                                                                    {f.name}
                                                                </button>
                                                            ) : ('')}
                                                        </td>
                                                        <td>{f.doc_type || 'Mixed'}</td>
                                                        <td>{f.sub_type || 'Mixed'}</td>
                                                        <td>{f.account_number || 'N/A'}</td>
                                                        <td>{f.dateDisplay || 'N/A'}</td>
                                                        <td>{typeof f.totalVectors === 'number' ? f.totalVectors : 'N/A'}</td>
                                                        <td>{typeof f.totalTextLength === 'number' ? `${f.totalTextLength} chars` : 'N/A'}</td>
                                                        <td>
                                                            <button className="btn btn-sm btn-outline-primary me-2" onClick={() => openDetailsModal(f.s3_key || f.name, f.file_id)}>View Vector Details</button>
                                                            <button className="btn btn-sm btn-outline-secondary" onClick={() => openSummaryModal(f.s3_key || f.name)}>View Document Summary</button>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {(sortedKbFiles || []).length === 0 && (
                                                    <tr>
                                                        <td colSpan={8} className="text-muted">No files</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                                
                                {/* File summaries pagination controls */}
                                <PaginationControls
                                    currentCount={kbFilesSummary.length}
                                    totalCount={fileSummariesPagination.totalCount}
                                    hasMore={fileSummariesPagination.hasMore}
                                    onLoadMore={handleLoadMoreFileSummaries}
                                    loading={loadingMoreFileSummaries}
                                    itemType="files"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Custom Summary Modal */}
            {customSummaryModalOpen && (
                <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog">
                    <div className="modal-dialog modal-lg" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Custom Summary Prompt</h5>
                                <button type="button" className="btn-close" onClick={handleCloseCustomSummaryModal}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Client Prompts:</label>
                                    <select
                                        className="form-select"
                                        value={selectedClientPrompt}
                                        onChange={(e) => {
                                            const promptId = e.target.value;
                                            setSelectedClientPrompt(promptId);

                                            if (promptId) {
                                                // Find the selected prompt and populate the fields
                                                const selectedPrompt = clientTypePrompts.find(p => p.prompt_id === promptId);
                                                if (selectedPrompt) {
                                                    setSelectedPromptSystemPrompt(selectedPrompt.system_prompt || '');
                                                    setSelectedPromptPrompt(selectedPrompt.prompt || '');
                                                } else {
                                                    setSelectedPromptSystemPrompt('');
                                                    setSelectedPromptPrompt('');
                                                }
                                            } else {
                                                setSelectedPromptSystemPrompt('');
                                                setSelectedPromptPrompt('');
                                            }
                                        }}
                                        disabled={promptsLoading}
                                    >
                                        <option value="">Select a client prompt (optional)</option>
                                        {clientTypePrompts.map(prompt => (
                                            <option key={prompt.prompt_id} value={prompt.prompt_id}>
                                                {prompt.name}
                                            </option>
                                        ))}
                                    </select>
                                    {promptsLoading && (
                                        <div className="form-text text-muted">
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Loading client prompts...
                                        </div>
                                    )}
                                </div>

                                {selectedClientPrompt ? (
                                    <>
                                        {/* Show system prompt and prompt fields when a client prompt is selected */}
                                        <div className="mb-3">
                                            <label className="form-label">System Prompt:</label>
                                            <textarea
                                                className="form-control"
                                                rows={4}
                                                value={selectedPromptSystemPrompt}
                                                onChange={e => setSelectedPromptSystemPrompt(e.target.value)}
                                                placeholder="Enter system prompt..."
                                                style={{ fontSize: '14px', lineHeight: '1.4' }}
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Prompt:</label>
                                            <textarea
                                                className="form-control"
                                                rows={6}
                                                value={selectedPromptPrompt}
                                                onChange={e => setSelectedPromptPrompt(e.target.value)}
                                                placeholder="Enter prompt text..."
                                                style={{ fontSize: '14px', lineHeight: '1.4' }}
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        {/* Show custom text area when no client prompt is selected */}
                                        <div className="mb-3">
                                            <label className="form-label">Enter your custom prompt for the document summary:</label>
                                            <textarea
                                                className="form-control"
                                                rows={8}
                                                value={customSummaryPrompt}
                                                onChange={e => setCustomSummaryPrompt(e.target.value)}
                                                placeholder="Example: Provide a detailed financial analysis of this document, focusing on investment recommendations and risk assessments..."
                                                style={{ fontSize: '14px', lineHeight: '1.4' }}
                                            />
                                        </div>
                                    </>
                                )}
                                <div className="text-muted small">
                                    <i className="bi bi-info-circle me-1"></i>
                                    The AI will analyze the document content using your custom prompt to generate a tailored summary.
                                </div>
                                <div className="text-muted small">
                                    <i className="bi bi-exclamation-circle me-1"></i>
                                    Your custom prompt and resulting summary will not be saved off. Please manually save any custom prompts or summaries you wish to keep.
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={handleCloseCustomSummaryModal}>Cancel</button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleSubmitCustomSummary}
                                    disabled={selectedClientPrompt ?
                                        (!selectedPromptSystemPrompt.trim() && !selectedPromptPrompt.trim()) :
                                        !customSummaryPrompt.trim()
                                    }
                                >
                                    <i className="bi bi-magic me-2"></i>
                                    Generate Custom Summary
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {customSummaryModalOpen && <div className="modal-backdrop fade show"></div>}

            {/* File preview modal (reuses ClientHelper's behavior */}
            <FilePreviewModal
                isOpen={fileModalOpen}
                onClose={() => setFileModalOpen(false)}
                fileUri={fileModalUri}
                filename={fileModalFilename}
                fetchFileContent={fetchFileContent}
            />
        </div>
    );
};

export default VectorInspector;
